import { can } from '@misumi/shared'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FormAlert } from '../../components/auth/FormAlert'
import { IconPlus, LeftOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { Icon } from '../../components/ui/Icon'
import { useToast } from '../../components/ui/ToastProvider'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { useAuth } from '../../lib/auth/auth-context'
import { useRfqBundle } from '../../lib/rfq/rfq-bundle-context'
import type { RfqDraftMeta } from '../../lib/rfq/rfq-bundle-context'
import { CART_ADD_BATCH_MAX_LINES } from '../cart/cart-limits'
import { AddShippingAccountModal } from '../checkout/AddShippingAccountModal'
import { AddressFormModal } from '../checkout/AddressFormModal'
import {
  type AddressView,
  type ShippingAccountView,
  defaultShippingAccountId,
  toCreateAddressRequest,
  toCreateShippingAccountRequest,
} from '../checkout/checkout-domain'
import { AddPartsModal } from './AddPartsModal'
import { BulkAddPricedConfirmModal } from './BulkAddPricedConfirmModal'
import { ConfigurePartsModal } from './ConfigurePartsModal'
import type { ConfigurePartsTarget } from './ConfigurePartsModal'
import { MoveToNewRfqConfirmModal } from './MoveToNewRfqConfirmModal'
import { PartsReviewTable } from './PartsReviewTable'
import { RequestDetailsSection } from './RequestDetailsSection'
import { RequestSummaryRail } from './RequestSummaryRail'
import { RfqAnalysisOverlay } from './RfqAnalysisOverlay'
import { RfqCard } from './RfqCard'
import { RfqDetailSummaryRail } from './RfqDetailSummaryRail'
import { RfqLineConfiguratorModal } from './RfqLineConfiguratorModal'
import { RfqPreQuotePricingPreview } from './RfqPreQuotePricingPreview'
import { RfqShareModal } from './RfqShareModal'
import { RfqSubmissionStatusBanner } from './RfqSubmissionStatusBanner'
import { searchAddPartsCandidates } from './add-parts-search'
import {
  isBulkCartWarnSuppressed,
  shouldSuppressBulkCartWarn,
  suppressBulkCartWarn,
} from './bulk-cart-warn-preference'
import { addLinesToCart, summarizeCartAddBatch } from './cart-add'
import type { RfqLineSpecification } from './configuration-estimate'
import { rfqNumberDisplay } from './domain'
import type { QuoteVariant, Rfq, RfqStatus } from './domain'
import {
  ensureLinesOnServerDraft,
  serverLineIdsOf,
  specSelectionsOf,
} from './ensure-lines-on-server-draft'
import {
  allLinesPriced,
  buildCreateRequest,
  buildMoveToNewRfqRequest,
  buildUpdateRequest,
  canSubmitRfq,
  cartEligibleLineIds,
  cartEligibleLines,
  needsConfigureLineIds,
  needsQuoteLineCount,
  nextNeedsConfigureLineId,
  partProductionLineCount,
  partProductionSubtotal,
  summarizeRequest,
  toReviewLine,
  validateNeedByDate,
  validateShipping,
} from './review-domain'
import type { RfqReviewLine, RfqReviewService, SpecSelection } from './review-domain'
import type { AddPartsCandidateRow } from './rfq-append-lines'
import {
  type DraftAttempt,
  type FreshDraft,
  isStaleDraftProblem,
  withFreshDraftRetry,
} from './stale-draft-recovery'
import { useRfqSubmissionStatus } from './use-rfq-submission-status'
import { useRfqAnalysis } from './useRfqAnalysis'

type RfqRegion = components['schemas']['RfqRegion']
type RfqShipMethod = components['schemas']['RfqShipMethod']
/** UID-401 (ADR-0125 D5) — the server-derived shipping/tax/total estimate,
 * ALWAYS emitted on `RfqView.costEstimate`. Read off every RfqView this page
 * receives (create/PATCH-sync/bulk-configure) and handed straight to
 * `RequestSummaryRail` — never re-derived or summed here. */
type RfqCostEstimate = components['schemas']['RfqCostEstimate']
/** The full, current view of one RFQ — what every write on this page
 * (create / append / bulk-configure / PATCH-sync) returns. */
type RfqView = components['schemas']['RfqView']

/** Today's date as YYYY-MM-DD in the browser's LOCAL calendar day (code-review
 * follow-up) — NOT `toISOString()`, which is UTC and, for anyone west of UTC
 * in the evening, reads as tomorrow: the native date input's value is the
 * user's local day, so comparing it against a UTC "today" can reject the
 * user's actual today as "in the past." */
function todayIsoDate(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * RFQ review screen (UID-56, screens.md §15, `/rfq/new`) — build → review →
 * submit a quote request as one single-page form (not a literal wizard).
 * Composes the parts table, targeting card, add-on services card, and
 * request-summary rail; owns the two-call submit sequence (`POST /rfq` to
 * create the draft, then `POST /rfq/{rfqId}/submit`) and navigates to the
 * UID-55 list (`/rfq`) on success.
 *
 * Gated on `rfq:submit` (Engineer-only, ADR-0042 / this screen's contract):
 * mounted inside the vertical registry's `<RequireAuth>` layout, so by the
 * time this renders `auth.status` is already resolved — the `loading`
 * branch below only matters for direct/isolated rendering (tests).
 *
 * UID-112/UID-119 — the parts table's identity IS `useRfqBundle()`'s queue,
 * not a page-local copy of it: this page has no `lines` state of its own.
 * An earlier version seeded page-local state from the bundle on mount and
 * immediately cleared the bundle — but this page is mounted fresh every time
 * the user navigates here (a route change unmounts it), while the bundle
 * lives ABOVE the router (`App.tsx`) and survives navigation. Seed-then-clear
 * meant a SECOND "Add to RFQ" trip from Library/BOM only ever saw parts added
 * since the FIRST trip — the first batch, already on the table, was silently
 * dropped instead of accumulating into "the same draft" (AC #1; filed as
 * UID-119). Deriving `lines` straight from the bundle on every render fixes
 * this by construction: there is only ever one list, and it's the same one
 * Library/BOM are writing into, however many trips the user makes. The
 * bundle is cleared only once a real RFQ is actually submitted — never
 * merely on arriving at this screen.
 */
// Every `RfqStatus` value that means "no longer editable as a draft" —
// used by the hard-reload rehydration effect below to decide whether a
// resumed slot's underlying RFQ has already left `draft`. A closed,
// explicit set, not `!== 'draft'`: an unrecognized/malformed response must
// never be treated as "already submitted" by default.
const NON_DRAFT_RFQ_STATUSES: ReadonlySet<RfqStatus> = new Set([
  'requested',
  'quoting',
  'quoted',
  'accepted',
  'declined',
  'expired',
])

export function RfqReviewPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const rfqBundle = useRfqBundle()
  const { showToast } = useToast()

  // ADR-0049 (UID-113): needByDate/region/notes/quantityOverrides/services no
  // longer live in this page's local `useState` — they're read from
  // `rfqBundle.draft` and written via `rfqBundle.setDraft`, exactly like
  // `lines` already derives from `rfqBundle.items` (UID-112/UID-119). This is
  // what lets "Resume" from the RFQ list's Draft tab return here with the
  // WHOLE in-progress request intact, not just the parts.
  const {
    needByDate,
    region,
    notes,
    quantityOverrides,
    services,
    shipMethod,
    shippingAccountId,
    shipToAddressId,
    specSelections,
  } = rfqBundle.draft
  // UID-260 (ADR-0085) — the Configure workflow's target line ids: `[id]` for a
  // per-row Configure click, every still-needing-it selected id for the bulk
  // toolbar action, or every needing-it line for the summary rail's "Configure"
  // CTA (opens the bulk workflow across the whole request). `undefined` means
  // the modal is closed.
  const [configureTargetIds, setConfigureTargetIds] = useState<readonly string[] | undefined>(
    undefined,
  )
  // UID-434/WS-D — the PER-LINE entry point's own target: the per-row
  // Configure button and the Requirements-cell pencil both set this (via
  // `PartsReviewTable`'s `onConfigureLine`), never `configureTargetIds`
  // above — that state is now BULK-toolbar-only. `undefined` means the
  // per-line modal is closed.
  const [configureLineId, setConfigureLineId] = useState<string | undefined>(undefined)
  // UID-260 gap #1/#2 follow-up — the PREVIEW-ONLY "Choose a quote option"
  // selection (`RfqPreQuotePricingPreview`). Page-local, ephemeral UI state
  // (not persisted to the bundle, unlike `specSelections` etc.) — it never
  // reaches the wire and is never a real Quote (ADR-0051); resetting to
  // `undefined` on remount is fine.
  const [previewVariantId, setPreviewVariantId] = useState<QuoteVariant['id'] | undefined>(
    undefined,
  )
  // UID-309 (`Fictiv Platform.dc.html:9675`/`:9657`) — the parts card's
  // filter chips: `partsFilterTabs` in the reference are REAL click-to-
  // filter controls over `filteredComposeItems`/`displayItems`, not the
  // inert "N priced"/"N to configure" `<span>`s this screen used to render.
  // Chip 1 is an UNFILTERED "All parts (N)" bound to `rowsData.length` (the
  // reference's `pf==='all'` case just returns `composeItems` unfiltered) —
  // a genuinely different population from `pricedCount` (the priced
  // SUBSET), which is why relabeling the old spans would have been wrong.
  // Chip 2 mirrors the reference's `configCount0`/`pf==='config'` case,
  // which filters on `it.notCart` — this codebase's equivalent predicate is
  // `needsConfigure` (review-domain.ts), already used for the identical
  // "still needs Configure" gating elsewhere on this page.
  const [partsFilter, setPartsFilter] = useState<'all' | 'config'>('all')
  const [needByDateError, setNeedByDateError] = useState<string | undefined>()
  const [shipAccountError, setShipAccountError] = useState<string | undefined>()
  // UID-201 (ADR-0076): the shared account books the compose pickers read — the tenant's saved
  // ship-to address book (`/account/addresses`) and shipping-account book
  // (`/account/shipping-accounts`, masked view only). Fetched on mount; non-fatal on failure
  // (the pickers simply render empty + their "＋ Add" affordance, mirroring checkout).
  const [addresses, setAddresses] = useState<readonly AddressView[]>([])
  const [shippingAccounts, setShippingAccounts] = useState<readonly ShippingAccountView[]>([])
  const [showAddShippingAccount, setShowAddShippingAccount] = useState(false)
  const [showAddAddress, setShowAddAddress] = useState(false)
  // UID-115: unlike needByDate/region/notes/services, noServicesAck is NOT
  // lifted into rfqBundle.draft — it never reaches the wire (buildCreateRequest
  // drops it; see handleNoServicesAckChange's comment) and this page already
  // remounts fresh (local state resets) whenever the bundle itself resets, so
  // there's no "state intact on Resume" requirement for it the way ADR-0049
  // requires for the wire-relevant fields.
  const [noServicesAck, setNoServicesAck] = useState(false)
  const [addPartsOpen, setAddPartsOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>()
  // UID-586 — set once `handleSubmit` succeeds, and never cleared while this
  // page stays mounted: this is what keeps the user ON this screen instead
  // of the old `navigate('/rfq')`. `postSubmitRfq` seeds
  // `useRfqSubmissionStatus`'s poll with the full `POST /rfq/{rfqId}/submit`
  // response (already a whole `RfqView` — no extra fetch needed to seed
  // it).
  const [postSubmitRfq, setPostSubmitRfq] = useState<Rfq | undefined>()
  // `RfqSubmissionStatusBanner`'s relative timestamp — captured once, at the
  // moment of submission, never from a later poll response.
  const [postSubmitAt, setPostSubmitAt] = useState<number | undefined>()
  const [selectedVariantId, setSelectedVariantId] = useState<QuoteVariant['id'] | undefined>()
  const submittedSlotIdToClearRef = useRef<string | undefined>(undefined)
  // UID-611 review round (finding 9, ADR-0179 D3) — `mountedRef` guards the
  // cascade-delete navigation just below: `liveRfq`'s poll (`useRfqSubmissionStatus`)
  // can still be in flight when the buyer navigates away from this page by some
  // OTHER route (Back, a header link, …), and a stale poll response landing after
  // unmount must not fire a SECOND, unwanted `navigate('/rfq')` on top of wherever
  // the buyer already went.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  // The LIVE, polled Rfq (status + full pricing once quoted) — distinct from
  // `postSubmitRfq` above, which is only the seed. `RfqCard`/
  // `RfqDetailSummaryRail` (below) need the WHOLE object, not just
  // `.status`: real per-line pricing, images, and the region/variant picker
  // all come from here, never from the draft-only `PartsReviewTable`/
  // `RequestSummaryRail` (which have no path to a real server-priced line at
  // all).
  const liveRfq = useRfqSubmissionStatus(postSubmitRfq)
  const postSubmitRfqId = postSubmitRfq?.id
  /** UID-586 — `RfqDetailSummaryRail`'s own refetch contract: called with no
   * argument to re-GET, or with an already-fetched `Rfq` to hydrate from a
   * response the caller (Checkout, accept, etc.) already has, avoiding a
   * redundant round trip — the SAME contract `RfqDetailPage` gives it. */
  async function refetchPostSubmitRfq(fresh?: Rfq) {
    if (fresh) {
      setPostSubmitRfq(fresh)
      return
    }
    if (postSubmitRfqId === undefined) return
    const { data } = await apiClient.GET('/rfq/{rfqId}', {
      params: { path: { rfqId: postSubmitRfqId } },
    })
    if (data) setPostSubmitRfq(data)
  }
  // UID-119's original fix (below, `handleSubmit`) removed the slot the
  // MOMENT submit succeeded, so a later "Add to RFQ" elsewhere starts a
  // fresh draft rather than resurrecting this one. Keeping the user on THIS
  // screen post-submit (UID-586) means that removal can no longer happen
  // synchronously in `handleSubmit` without also blanking `lines` (which
  // reads live off the slot) out from under the very banner/table this page
  // now keeps showing. Deferred to unmount instead — still exactly once,
  // still before any OTHER draft could be started from a stale reference to
  // this one, because unmounting this page is a precondition for navigating
  // to start a new one.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount/unmount-only, deliberately — this must fire ONCE, on this page's own teardown, never mid-life on a `rfqBundle` identity change.
  useEffect(() => {
    return () => {
      if (submittedSlotIdToClearRef.current !== undefined) {
        rfqBundle.clearSlot(submittedSlotIdToClearRef.current)
      }
    }
  }, [])
  // UID-114/UID-262: line ids currently mid "Move to a new RFQ" (the draft-
  // create round trip) — passed to PartsReviewTable so it can disable that
  // row's menu trigger while in flight; `moveError` surfaces a create failure
  // (a success navigates away, see handleMoveToNewRfq).
  const [movingLineIds, setMovingLineIds] = useState<readonly string[]>([])
  const [moveError, setMoveError] = useState<string | undefined>()
  // UID-114/UID-262 row menu: which line, if any, is mid-confirm for "Move to
  // a new RFQ" (the row menu only asks — the round trip runs on Confirm, never
  // straight off the menu click). Opens `MoveToNewRfqConfirmModal` below.
  const [pendingMoveLineId, setPendingMoveLineId] = useState<string | undefined>()
  // UID-263 (ADR-0087 D2=FULL/D3) — the bulk "Add N priced to cart" button's
  // own state: `bulkAdding` is the in-flight guard (disables the button +
  // early-returns the handler, ADR-0087 D3's double-submit guard) and drives
  // `RequestSummaryRail`'s "Adding to cart…" label. The batch-outcome
  // announcement follows a nonce pattern (ADR-0064 carry-through) so a
  // screen reader re-announces even a repeated message.
  const [bulkAdding, setBulkAdding] = useState(false)
  // UID-401 Part 2 — the ONE confirm-modal open/intent state, now shared by
  // THREE callers: the pre-existing "Add N priced to cart" button
  // (`'plain'`, skippable via `ff_hide_bulk_cart_warn`, ADR-0087 D5) and the
  // new Checkout (`'checkout'`) / "Generate quote" (`'generate-quote'`) CTAs
  // (never skippable — see `handleCheckoutClick`'s doc comment for why).
  // `null` means closed; the modal's own `open` prop is derived from this
  // being non-null, so there is exactly one source of truth for whether it's
  // showing AND which flow will run on confirm.
  const [bulkConfirmIntent, setBulkConfirmIntent] = useState<
    'plain' | 'checkout' | 'generate-quote' | null
  >(null)
  const bulkConfirmOpen = bulkConfirmIntent !== null
  const [bulkAnnouncement, setBulkAnnouncement] = useState<{ text: string; nonce: number }>({
    text: '',
    nonce: 0,
  })
  // ADR-0082 (UID-242 Item 1): the submit-time idempotency token that used to live here
  // as page-local `createdRfqId` (invalidated on every edit by `invalidateDraft`) is
  // GONE — replaced by `rfqBundle.draftRfqId`, a STABLE id minted eagerly on the 0→1
  // parts transition (see the effect below) and kept valid across edits because it's
  // synced by `PATCH /rfq/{rfqId}` at Share/Submit time instead of being thrown away.
  // `rfqBundle.draftRfqId` lives in the bundle store (not here) so it survives this
  // page unmounting/remounting on navigate-away/back (ADR-0049's precedent for `draft`).
  const { draftRfqId, draftRfqNumber, creatingDraft, activeSlotId, beginDraft } = rfqBundle
  // UID-552 (ADR-0170) — this page is the compose screen for WHICHEVER draft
  // is active; every producer entry point now calls `beginDraft` before
  // routing here, so by the time this mounts there is normally already one.
  // The one case that is NOT true: landing here with no active slot at all
  // (a direct/bookmarked visit to `/rfq/new`, or the Draft tab's own
  // zero-state "Start a new RFQ" button) — this page is ALSO a legitimate
  // place to start a draft from scratch, exactly like the old
  // single-shared-bundle store always let you do. There has to be a slot
  // BEFORE the buyer's first keystroke, not just before their first added
  // part: `rfqBundle.setDraft` (need-by date, region, notes, shipping) is a
  // silent NO-OP when no slot is active, so without this the metadata typed
  // on an empty compose screen would be dropped on the floor.
  //
  // ⚠️ Code-review C1 — REF-GUARDED, and that is load-bearing. The previous
  // version keyed on `[activeSlotId, beginDraft]` and re-checked
  // `activeSlotId === undefined` on every run, which minted TWO phantom
  // slots per visit whenever a run was discarded or replayed before
  // `beginDraft`'s state commit landed (React StrictMode's deliberate
  // double-invoke does exactly this). A ref survives that replay, so this
  // can fire at most ONCE per mount no matter how the render/effect
  // scheduling churns. The empty slot it mints is invisible to the Draft tab
  // and to both badges (`isOpenDraftSlot` filters it — that is C1's other
  // half) and is pruned by the next real `beginDraft`, so it can neither be
  // counted nor accumulate.
  const mintedScratchSlotRef = useRef(false)
  // Mount-only BY DESIGN — re-running this on `activeSlotId` is precisely the
  // defect C1 fixed (see the ref-guard note above), so the empty dep array is
  // the fix, not an oversight. `beginDraft` is a stable `useCallback` with an
  // empty dep list.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above.
  useEffect(() => {
    if (mintedScratchSlotRef.current) return
    mintedScratchSlotRef.current = true
    if (activeSlotId === undefined) {
      beginDraft([])
    }
  }, [])
  // Defect fix — a hard browser reload while this page is mid-transition
  // (status already `requested`/`quoting`/`quoted`) restarts `postSubmitRfq`
  // as `undefined`: that state is set only by `handleSubmit`'s own success
  // path, in-memory, never persisted. Left unchecked, this page falls back
  // to rendering the resumed slot's STALE, pre-submission local draft data
  // as if it were still editable — and the Draft tab, which has no
  // server-status awareness of its own, keeps showing this same RFQ as an
  // open draft, because the unmount-deferred `clearSlot` (above) never runs
  // either: a hard reload tears down the page with no React unmount to fire
  // it. Runs ONCE, at mount, reading the slot/id that were ALREADY active
  // at that point (ref-captured, mirroring the scratch-slot guard above) —
  // a slot minted fresh during this same session by the effect above is
  // genuinely `draft`, so re-fetching it here is a harmless no-op.
  const initialDraftRfqIdRef = useRef(draftRfqId)
  const initialActiveSlotIdRef = useRef(activeSlotId)
  useEffect(() => {
    const rfqId = initialDraftRfqIdRef.current
    if (rfqId === undefined) return
    let cancelled = false
    void (async () => {
      const { data } = await apiClient.GET('/rfq/{rfqId}', { params: { path: { rfqId } } })
      // Whitelisted, not `!== 'draft'` — a response this page doesn't
      // recognize (a malformed/partial fixture in a test, or a future wire
      // shape this code hasn't been taught yet) must never be treated as
      // "already submitted" by default; `undefined !== 'draft'` is also
      // true, which a bare negative check would wrongly act on.
      if (cancelled || !data || !NON_DRAFT_RFQ_STATUSES.has(data.status)) return
      setPostSubmitRfq(data)
      setPostSubmitAt(data.submittedAt ? Date.parse(data.submittedAt) : Date.now())
      if (initialActiveSlotIdRef.current !== undefined) {
        submittedSlotIdToClearRef.current = initialActiveSlotIdRef.current
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])
  // UID-242 Item 1: true while the share-time `PATCH /rfq/{rfqId}` sync is in flight —
  // page-local (unlike `draftRfqId`) because it's pure UI busy-state, not something a
  // navigate-away/back needs to resume.
  const [sharing, setSharing] = useState(false)
  // UID-466 (ADR-0152) — the share modal `handleShare` opens once its PATCH sync
  // lands. Separate from `sharing` above, which is only the in-flight flag for
  // that PATCH (and still drives the rail button's spinner).
  const [shareOpen, setShareOpen] = useState(false)
  // UID-346 (ADR-0117 D11, 2026-08-27) — the sourceable ids known to exist as
  // lines on the server draft, so `handleSaveConfigure` can call the shared
  // `ensureLinesOnServerDraft` seam (`ensure-lines-on-server-draft.ts`) for
  // exactly the ones that don't yet, instead of every call re-appending or
  // (worse) PATCH-resyncing the whole array (W6). Deliberately PAGE-LOCAL
  // (unlike `draftRfqId`): a stale/empty value on remount only costs the seam
  // one extra, correctly-reconciled append attempt — never an incorrect
  // outcome, since the seam re-reads the server on any conflict — so there is
  // no correctness reason to thread this through the bundle store too.
  const [serverLineIds, setServerLineIds] = useState<ReadonlySet<string>>(new Set())
  /** Full-replace from the latest authoritative `RfqView` a network call
   * returned (create/append/bulk-configure/GET all return the complete,
   * current line set) — never a union, because a full response is always
   * more current than whatever this held before. Shares `serverLineIdsOf`
   * with the seam itself so there is one reducer for "ids on an RfqView". */
  function recordServerLines(view: Parameters<typeof serverLineIdsOf>[0]) {
    setServerLineIds(serverLineIdsOf(view))
  }
  // UID-401 (ADR-0125 D5) — the compose rail's Shipping/Tax/Total rows read
  // ONLY this state. `undefined` until the first server round trip resolves
  // (before any draft exists there is nothing to derive an estimate from).
  const [costEstimate, setCostEstimate] = useState<RfqCostEstimate | undefined>(undefined)
  /** Every RfqView-returning response this page receives ALWAYS carries
   * `costEstimate` (server-emitted unconditionally, see `toWireRfq`) — this
   * is the ONE place that reads it off a response and stores it, so every
   * caller (create/PATCH-sync/bulk-configure) stays in sync by construction
   * rather than each hand-rolling its own `setCostEstimate` call correctly
   * or not. */
  function recordCostEstimate(view: { readonly costEstimate?: RfqCostEstimate }) {
    if (view.costEstimate) setCostEstimate(view.costEstimate)
  }
  /**
   * UID-530 AC3 — the FOURTH server-derived fact harvested off an
   * `RfqView`-returning response, alongside `rfqNumber`, `recordServerLines`
   * and `recordCostEstimate`.
   *
   * The server resolves a library part's saved configuration and SNAPSHOTS it
   * onto the line at write time (the sibling of ADR-0063's `requirements` and
   * ADR-0138's part identity). The compose screen's Configure gate reads
   * `draft.specSelections`, whose only writer used to be the Configure modal —
   * so a part the buyer had already configured in the Parts Library read "Needs
   * configuration" and blocked submit even though the persisted request was
   * fully configured.
   *
   * Unlike its three siblings this writes the BUNDLE STORE, not page-local
   * state, because that is where `specSelections` lives (it must survive
   * navigate-away/back, ADR-0049). The store's `hydrateSpecSelections` owns the
   * merge and its EXISTING-WINS precedence — a harvest never clobbers a
   * selection the buyer just authored in the modal.
   *
   * TRUST BOUNDARY: nothing is trusted here that was not server-derived to
   * begin with. The hydrated value came FROM the server, and if it is echoed
   * back on a later PATCH/submit, `buildLineRequest` sends only
   * `{specification}` — which the server re-validates through
   * `parseSpecification` and re-prices through `buildSpecSelection`, so every
   * price field is re-derived server-side regardless of what rode the wire.
   */
  function recordSpecSelections(view: Parameters<typeof specSelectionsOf>[0]) {
    rfqBundle.hydrateSpecSelections(specSelectionsOf(view))
  }

  const needByDateInputRef = useRef<HTMLInputElement | null>(null)
  const shipAccountErrorRef = useRef<HTMLParagraphElement | null>(null)
  const alertRef = useRef<HTMLDivElement | null>(null)
  const moveAlertRef = useRef<HTMLDivElement | null>(null)

  // UID-201 (ADR-0076): fetch the tenant's saved address + shipping-account books on mount so
  // the compose pickers can render + select from them (mirrors checkout's mount loads). Both
  // reads are non-fatal — a failure just leaves the picker empty (its "＋ Add" affordance still
  // works). `setDraft` is stable (useCallback in the bundle store), so the empty dep array runs
  // this exactly once per mount; this page remounts on every navigation here.
  const { setDraft: setDraftForLoad } = rfqBundle
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only load; setDraft is stable.
  useEffect(() => {
    let mounted = true
    void (async () => {
      const [addr, accts] = await Promise.all([
        apiClient.GET('/account/addresses').catch(() => ({ data: undefined })),
        apiClient.GET('/account/shipping-accounts').catch(() => ({ data: undefined })),
      ])
      if (!mounted) return
      if (addr.data) setAddresses(addr.data.addresses)
      if (accts.data) {
        setShippingAccounts(accts.data.shippingAccounts)
        // Pre-select the book's default account only when own-account is already the resumed
        // draft method and nothing is selected yet — so an own-account draft has a resolvable
        // reference without an extra click (checkout's default-on-load parity).
        const current = rfqBundle.draft
        if (current.shipMethod === 'own_account' && current.shippingAccountId === '') {
          const fallback = defaultShippingAccountId(accts.data.shippingAccounts)
          if (fallback) setDraftForLoad({ shippingAccountId: fallback })
        }
      }
    })()
    return () => {
      mounted = false
    }
  }, [setDraftForLoad])

  // UID-201 (ADR-0076): move focus to the own-account validation error once it appears (the
  // error <p> is conditional, so it only exists AFTER the render that sets the error — a
  // synchronous focus in handleSubmit would miss it). Fires only when the error is set.
  useEffect(() => {
    if (shipAccountError) shipAccountErrorRef.current?.focus()
  }, [shipAccountError])

  // `lines` is computed here (ahead of the auth early-returns below) — NOT because it's
  // needed for rendering yet, but because the eager-create effect right after it is a
  // HOOK: React requires every hook to run in the same order on every render, so it must
  // sit above any conditional `return`, exactly like the two effects above it.
  //
  // Gate 5 C2 — MEMOIZED off its three real inputs, deliberately NOT a fresh
  // `.map()` on every render. `RfqLineConfiguratorModal`'s own `subject` memo
  // (`useMemo(() => fromRfqReviewLine(line), [line])`) — and, transitively,
  // `PartConfiguratorShell`'s `initial` memo and `[open, initial]` reset
  // effect — depend on this page handing back the SAME `RfqReviewLine`
  // object reference across a render that hasn't actually changed that
  // line's data. An unmemoized `.map()` fails that on EVERY render (a fresh
  // line object every time, `Object.is`-unequal to the last one even when
  // nothing about the line changed), which — after this ticket's "don't
  // close on every save" fix made "the modal stays open across a re-render"
  // the NORMAL post-save path rather than an unreachable one — reset every
  // in-progress field, the tab, and the just-shown success banner on any
  // unrelated re-render of this page (a sibling quantity change, a toast
  // timer, `handleSaveConfigure`'s own post-save `rfqBundle.setDraft` +
  // `showToast`). Memoizing here is what makes `RfqLineConfiguratorModal`'s
  // own doc comment (see that file) actually true.
  const lines: readonly RfqReviewLine[] = useMemo(
    () =>
      rfqBundle.items.map((item) => ({
        id: item.sourceable.id,
        sourceable: item.sourceable,
        name: item.name,
        sku: item.sku,
        quantity: quantityOverrides[item.sourceable.id] ?? 1,
        // UID-260 (ADR-0085) — mirrors `quantityOverrides`'s keyed-by-sourceable-id
        // lookup: absent entry = "needs Configure" (null), same gating signal the
        // server's `RfqLineView.specSelection` uses.
        specSelection: specSelections[item.sourceable.id] ?? null,
      })),
    [rfqBundle.items, quantityOverrides, specSelections],
  )

  // UID-611 review round (ADR-0179 D3 "draft-compose parity", finding 2) —
  // tracks every id removed via `handleRemoveLine` (below) SINCE THIS
  // RENDER's `lines` snapshot was taken, so a bulk batch (`PartsReviewTable`'s
  // "Delete selected", which calls `onRemove(id)` once per selected id in a
  // single synchronous loop — see that file's own doc comment on this exact
  // gap) can be detected as "emptied the whole draft" even though
  // `rfqBundle.items` itself does not update synchronously within that loop
  // (same reasoning `removeLinesFromRfqAndMaybeDiscard` above already
  // documents for its own `ids.length === lines.length` check). Reset once a
  // render actually lands (the effect below, keyed on `lines`) so it never
  // accumulates ACROSS unrelated, separately-rendered removals. Declared
  // HERE (immediately after `lines`, and unconditionally, before this
  // component's `auth.status` early-return guards further down) — NOT next
  // to `handleRemoveLine` itself, which sits after those guards; a hook
  // declared there would only run on renders that pass them, a real Rules-
  // of-Hooks violation this file's OWN early draft of this fix tripped
  // (`Rendered more hooks than during the previous render`, caught by this
  // fix's own test run before it ever reached review).
  const removedLineIdsThisBatchRef = useRef<Set<string>>(new Set())
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — reset WHEN lines changes; the body doesn't read it (stable ref), same rationale as this file's other reset effects.
  useEffect(() => {
    removedLineIdsThisBatchRef.current = new Set()
  }, [lines])

  // ADR-0082 (UID-242 Item 1) — eager draft creation on the compose screen's 0→1 parts
  // transition. Lives HERE (a `RfqReviewPage` effect), deliberately NOT inside
  // `rfqBundle.addItems` — putting it there would mint a server-side RFQ draft every
  // time the Library/BOM screens queue a part, even for users who never reach this
  // screen or never compose an RFQ at all, which is a much larger orphan-draft blast
  // radius than this ticket intends (ADR-0082 §3).
  //
  // Guard is EXACTLY `lines.length > 0 && !draftRfqId && !creatingDraft` (fires once, on
  // the 0→1 transition, not on every subsequent part added — 1→2, 2→3, ... are no-ops
  // here because `draftRfqId` is already set by then). `creatingDraft` is set to `true`
  // BEFORE the `await` (best-effort race guard — two rapid "add part" events landing in
  // the same tick must not both pass the guard and mint two drafts; a real server
  // `Idempotency-Key` header would harden this further but is out of scope, ADR-0082 §3).
  //
  // Failure is NON-FATAL by design: on a failed `POST /rfq`, `creatingDraft` is cleared
  // and `draftRfqId` is left unset — no error banner, compose is never blocked. The next
  // effect run (triggered by any subsequent state change while `lines.length > 0` still
  // holds) retries lazily; if it never succeeds, Submit's own create-at-submit fallback
  // (`handleSubmit` below) still creates the draft at that point, exactly as it did
  // before this ADR. "Share for review" simply stays disabled until an id exists.
  const { setSlotRfqId, setSlotRfqNumber, setSlotCreating, slots } = rfqBundle
  // Deliberately keyed ONLY on `lines.length`/`activeSlotId` — NOT on
  // `draftRfqId`/`creatingDraft` too, even though the guard used to read them, and NOT
  // on needByDate/region/notes/services/shipping. Two reasons:
  //  1. Self-cancellation bug (found in review): flipping a "creating" flag inside this
  //     effect must not itself be a dependency of the SAME effect, or React would run
  //     its cleanup (before the in-flight `POST /rfq` even resolves) and the next
  //     effect instance's `created.data` handler would see a soon-to-be-set-false
  //     "active" flag and silently drop the result, so the id would never actually
  //     land. This is why the guard below reads the CAPTURED slot's own `rfqId`/
  //     `creating` fields fresh off `rfqBundle.slots` each run, rather than depending
  //     on the flat active-slot projections.
  //  2. `needByDate`/etc. are read fresh (closed over) at the moment `lines.length`/
  //     `activeSlotId` last changed — matching ADR-0082's "created with only the first
  //     part and default metadata" framing; re-including them would also re-fire this
  //     on every keystroke while a part is already queued.
  //
  // ⚠️ UID-552 (ADR-0170) ASYNC RACE FIX — `slotId` is captured HERE, before the
  // `await` below, and every write after that `await` targets THIS captured id via
  // `setSlotRfqId`/`setSlotRfqNumber`/`setSlotCreating` (never the flat
  // `setDraftRfqId`/`setCreatingDraft` active-slot forms). Without this, a user who
  // navigates away and begins a DIFFERENT draft while this `POST /rfq` is still in
  // flight would have the eventual response land on the NEW active slot instead of
  // the one it was actually created for — corrupting both drafts. If the captured
  // slot has since been discarded, these writes are harmless no-ops (see
  // `setSlotRfqId`'s doc comment) — never resurrecting a discarded slot.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see the comment above.
  useEffect(() => {
    const slotId = activeSlotId
    if (slotId === undefined) return
    const slot = slots.find((candidate) => candidate.slotId === slotId)
    if (!slot || lines.length === 0 || slot.rfqId || slot.creating) return
    setSlotCreating(slotId, true)
    void (async () => {
      try {
        const created = await apiClient.POST('/rfq', {
          body: buildCreateRequest({
            lines,
            needByDate,
            region,
            notes,
            services,
            noServicesAck,
            shipMethod,
            shippingAccountId,
            shipToAddressId,
          }),
        })
        if (created.data) {
          setSlotRfqId(slotId, created.data.id)
          // UID-309 — the same create response carries the server-derived sequential
          // number (ADR-0093 C1); thread it into the store alongside the id so the
          // heading below can show `RFQ-2026-0003` instead of the generic compose
          // title as soon as the draft exists. `rfqNumberDisplay`'s uuid-fragment
          // fallback covers the (present-only-when-derivable) case where the server
          // omitted it.
          setSlotRfqNumber(slotId, created.data.rfqNumber ?? undefined)
          // UID-346 (2026-08-27 follow-up) — this create rides only the line(s)
          // present at the 0→1 transition (usually one); record them as known-on-
          // server so a LATER Configure on just this line never appends redundantly.
          recordServerLines(created.data)
          // UID-401 (ADR-0125 D5) — the eager-create response already carries the
          // server-derived estimate for the draft's default shipping/tax state.
          recordCostEstimate(created.data)
          // UID-530 AC3 — …and the per-line configuration the server snapshotted
          // from the tenant parts library for any already-configured part.
          recordSpecSelections(created.data)
        }
      } catch {
        // Non-fatal (ADR-0082 §4) — a thrown network error is treated exactly like a
        // structured `{ data: undefined }` failure response: no draft id, no banner,
        // lazy retry on the next effect run / Submit's create-at-submit fallback.
      } finally {
        // Cleared on BOTH success and failure — on success the guard's `slot.rfqId` leg
        // now blocks re-entry anyway; on failure this is what allows a lazy retry the
        // next time `lines.length` changes (e.g. another part added). Slot-targeted,
        // same as the writes above — see the ASYNC RACE note.
        setSlotCreating(slotId, false)
      }
    })()
  }, [lines.length, activeSlotId])

  // UID-261 — the "Preparing your RFQ" entry animation. A HOOK, so it must
  // sit above the auth early-returns below (same rule as the two effects
  // above it) even though its result is only rendered once we know we're
  // past them.
  const rfqAnalysis = useRfqAnalysis(lines)

  if (auth.status === 'loading') {
    return null
  }

  if (auth.status !== 'authenticated' || !can(auth.session.accountContext.role, 'rfq:submit')) {
    return (
      <section aria-labelledby="rfq-review-denied-heading">
        <h1 id="rfq-review-denied-heading" className="display-heading">
          You don&rsquo;t have permission to submit an RFQ
        </h1>
        <p>Only an Engineer on this account can build and submit a new RFQ request.</p>
      </section>
    )
  }

  const alreadyAddedIds = new Set(lines.map((line) => line.id))
  const summary = summarizeRequest(lines, services)
  // UID-260 (ADR-0085, design fact #4) — `needsConfigureIds`/`needsConfigCount`:
  // lines with NO specSelection at all — the Configure-gating signal that
  // drives the submit CTA (design fact #4's `needsConfigCount>0` rule) and
  // the bulk-configure-all target.
  //
  // Gap #5 (real bug fix): `needsConfigureIds` goes through the shared
  // `needsConfigure`/`needsConfigureLineIds` predicate (review-domain.ts),
  // which excludes `kind === 'catalog'` lines — a catalog line never carries
  // a `specSelection` and has nothing to Configure. Before this fix, ANY RFQ
  // containing a catalog part permanently showed "needs Configure" here and
  // blocked submission.
  //
  // UID-309 Gate 5 remediation — the header used to ALSO compute a
  // `pricedCount`/`unpricedCount` pair (`specSelection.unitPriceAmount`
  // present/absent) purely to feed two now-removed inert "N priced"/"N to
  // configure" `<span>`s. Those are replaced below by REAL click-to-filter
  // chips matching the reference (`Fictiv Platform.dc.html:9675`): "All
  // parts (N)" — unfiltered, `lines.length` — and "Needs configuration (N)",
  // which reuses `needsConfigCount`/`needsConfigureIds` directly rather than
  // a second, subtly-different priced/unpriced split (a configured-but-
  // quote-pending line is "unpriced" but NOT "needs Configure" — the
  // reference's own two chips never made that distinction either, see
  // `partsFilterTabs`'s `configCount0` — so there is no reference-mandated
  // reason to keep a separate priced/unpriced count alive here now that
  // nothing renders it).
  const needsConfigureIds = needsConfigureLineIds(lines)
  const needsConfigCount = needsConfigureIds.length
  // UID-309 — the chips' actual filtering: "All parts" is `lines` unfiltered
  // (matching the reference's `pf==='all'` case); "Needs configuration"
  // narrows to exactly the ids `needsConfigureIds` already identifies.
  const needsConfigureIdSet = new Set(needsConfigureIds)
  const filteredLines =
    partsFilter === 'config' ? lines.filter((line) => needsConfigureIdSet.has(line.id)) : lines
  // UID-263 (ADR-0087 D1(c)) — the bulk button's OWN set/visibility, bound
  // to `cartEligibleLines` (== `isCatalogLine`, the identical predicate the
  // per-row `+` button uses), NEVER to a priced/unpriced header count (a
  // disjoint, configured-custom population — left untouched by this
  // ticket). `showAddBulk` is hidden entirely (not disabled) once every
  // line is already cart-eligible — the design routes that case to the
  // Submit CTA instead (ADR-0087 D1(c)).
  const cartEligibleCount = cartEligibleLines(lines).length
  const allCartEligible = lines.length > 0 && cartEligibleCount === lines.length
  const showAddBulk = cartEligibleCount > 0 && !allCartEligible
  // UID-401 TASK D — the design's `quoteView`/`allPriced` gate
  // (`dc.html:~9687`) for the summary rail's "Checkout" CTA, lifted to the
  // shared `allLinesPriced` predicate (TASK A) so it can never disagree with
  // `isLinePriced`/`UnitPriceCell`'s per-row notion of "priced."
  //
  // Gate-5 C4 (UID-401) — `cartEligibleCount > 0` is REQUIRED alongside it, and
  // neither condition implies the other. `allLinesPriced` is satisfied by a
  // draft of only CONFIGURED AUTO-PRICED CUSTOM lines, while `cartEligibleLines`
  // is catalog-only and must not be widened (ADR-0087 D1: a configured-custom
  // line's `unitPriceAmount` is server-forced `null` pre-quote, so there is no
  // resolved price to add). On such a draft the CTA used to render, and
  // confirming it ran a ZERO-target batch: `targets = []` → no lines added → no
  // lines removed → `emptiedDraft` false → but `alwaysNavigate` navigated
  // anyway, landing the user on `/cart` with nothing added, under a confirm
  // modal reading "Moving 0 priced parts to your cart". That also falsified the
  // guarantee ADR-0125 D9 hands UID-403 in writing ("arrived from RFQ with
  // nothing added is no longer a case").
  //
  // Structurally the SAME lesson as D3's quote-options gate: two conditions,
  // neither implying the other, and collapsing to one reintroduces a defect.
  // This gate also governs the secondary button's "Generate quote" swap, so
  // both cart-bound CTAs are covered by one signal.
  const showCheckout = allLinesPriced(lines) && cartEligibleCount > 0
  // The session tenant, stamped onto tenant-owned parts at request-build time
  // (see toReviewLine) — the fixtures' fictional tenant id must never reach
  // the wire, or the server's tenant guards refuse the part downstream.
  const sessionAccountId = auth.session.accountContext.accountId

  // UID-552 (ADR-0170) — `addItems`, deliberately NOT `beginDraft`. This is the
  // ONE remaining caller of `addItems` in the app: the in-page "Add parts"
  // modal legitimately adds MORE parts to the draft the user is ALREADY
  // editing on this screen — it is not a new "Create RFQ"/"Add to RFQ" entry
  // point, so it must not start a second, separate draft. See `addItems`'s
  // doc comment on `RfqBundleValue` for the same distinction stated there.
  function handleAddParts(selected: readonly AddPartsCandidateRow[]) {
    const items = selected.map((candidate) => {
      const line = toReviewLine(candidate.sourceable, sessionAccountId)
      return { sourceable: line.sourceable, name: line.name, sku: line.sku }
    })
    // Code-review C1 — LAZY MINT, not a blanket mount-time one. `addItems` is
    // a silent no-op with no active slot, so this is the point at which a
    // slot genuinely MUST exist. The mount effect above normally already
    // provided one (it has to, for metadata edits); this is the belt to that
    // braces and, unlike a re-runnable mount effect, it can only ever fire
    // for a real user action that has parts to put in the slot — so it can
    // never leave an empty phantom behind.
    if (activeSlotId === undefined) {
      rfqBundle.beginDraft(items)
      return
    }
    rfqBundle.addItems(items)
  }

  // NB (code-review nit, ADR-0049): the setDraft calls below that compose the
  // next value from closed-over render-scope state (quantityOverrides here,
  // `services` in the add/remove handlers) rely on the invariant that each is a
  // discrete user event with a re-render in between — never two setDraft calls
  // batched in one tick. That holds for every current caller. If a future
  // caller ever issues two of these in a single tick, switch setDraft to a
  // functional-updater form; today the simple merge-patch is correct.
  function handleQuantityChange(id: string, quantity: number) {
    rfqBundle.setDraft({ quantityOverrides: { ...quantityOverrides, [id]: quantity } })
  }

  function handleRemoveLine(id: string) {
    // The cascade (dropping the removed part's quantity override + stripping
    // its id from every service's sourceableIds) now lives in the store
    // itself (ADR-0049) — this handler shrinks to a single call.
    rfqBundle.removeItem(id)
    removedLineIdsThisBatchRef.current.add(id)
    // ADR-0179 D3's "draft-compose parity" clause: this client-only draft
    // flow (no server RFQ row to cascade for a still-unsubmitted draft —
    // `rfqBundle.discard()` already no-ops the abandon POST when
    // `slot.rfqId` is `undefined`, and safely fires it when ADR-0082's eager
    // create DID mint one) applies the SAME "removing the last remaining
    // line drops the whole request" rule the submitted-RFQ server cascade
    // enforces. Compares against `lines.length` (THIS render's snapshot,
    // taken before any of this batch's removals — `lines` itself only
    // updates on the next render) rather than `rfqBundle.items`, for the
    // same reason `removeLinesFromRfqAndMaybeDiscard` above does.
    if (removedLineIdsThisBatchRef.current.size >= lines.length) {
      rfqBundle.discard()
      navigate('/rfq')
    }
  }

  /** UID-401 D8/D9 — the one-shot cart-arrival intent carried on
   * `location.state` when "Generate quote" is the flow that just ran the bulk
   * add. Consumed by UID-403 (cart vertical, NOT built here) to auto-open the
   * already-shared `cart/GenerateQuoteModal.tsx`.
   *
   * BINDING CONTRACT for UID-403 — do not rename this key or reshape this
   * object without updating that ticket's consuming side. UID-403 MUST also
   * CONSUME it (`navigate(pathname + search, { replace: true, state: null })`)
   * rather than merely read it: ADR-0124's W3 established that
   * `location.state` belongs to the HISTORY ENTRY, not the component, so an
   * unconsumed one-shot hand-off survives Back/forward and reload and
   * re-fires undismissably. */
  const GENERATE_QUOTE_CART_INTENT_STATE = { openGenerateQuoteModal: true } as const

  /** UID-263 (ADR-0087 D2=FULL, D3) / UID-401 D9 — the ONE place every caller
   * that removes lines from this draft after a successful cart add navigates
   * to `/cart`. Deliberately the SINGLE `navigate` call site for this page's
   * whole "lines went to the cart" family of flows.
   *
   * Two ways this ends at `/cart`, checked with `||` in ONE guard so this
   * function can never navigate twice for one invocation:
   *  1. `ids` emptied the WHOLE draft — the pre-existing per-row/plain-bulk
   *     behavior: `discard()` (abandon the eager server draft, UID-312 /
   *     ADR-0108 D10) and then navigate.
   *  2. `opts.alwaysNavigate` — UID-401's Checkout / "Generate quote" CTAs,
   *     which must land on `/cart` even in the mixed-draft case where
   *     cart-INeligible custom lines are deliberately left behind on a
   *     still-live (NOT discarded) RFQ.
   *
   * Routing the CTAs' intent THROUGH this function via `opts.navigateState`,
   * rather than letting them issue their own `navigate('/cart')` afterwards,
   * is what makes the generate-quote intent survive whichever branch actually
   * fires. A second navigate bolted on top would both double-navigate AND
   * land stateless in the all-catalog case (where branch 1 fires first),
   * losing the intent exactly in the most common case.
   *
   * `ids.length === lines.length` compares against `lines` — THIS render's
   * snapshot, taken before any of these removals — because
   * `rfqBundle.removeItem`'s state update does not resolve synchronously
   * within this closure.
   *
   * Gate 5 S3 (UPDATED by UID-401) — this "empty the whole draft" branch used
   * to be, in practice, reachable ONLY per-row: `showAddBulk`'s
   * `!allCartEligible` guard (ADR-0087 D1(c)) meant the original bulk button
   * only ever showed when a non-cart-eligible line would be LEFT BEHIND, so
   * even a full-success bulk batch could never remove every line. UID-401's
   * Checkout / "Generate quote" CTAs are THIRD and FOURTH callers and do NOT
   * go through that guard: on an all-catalog draft (`allCartEligible === true`,
   * which also trivially satisfies `allLinesPriced`) confirming one of them
   * removes EVERY line, making this branch genuinely reachable from a bulk
   * batch for the first time. Do NOT re-instate the old "bulk can never hit
   * this" assumption anywhere in this file. */
  function removeLinesFromRfqAndMaybeDiscard(
    ids: readonly string[],
    opts?: { readonly alwaysNavigate?: boolean; readonly navigateState?: unknown },
  ) {
    for (const id of ids) {
      rfqBundle.removeItem(id)
    }
    // See this function's doc comment for the Gate-5 S3 note, which UID-401
    // UPDATED: the empty-the-whole-draft branch below is no longer per-row-only.
    //
    // UID-312 (ADR-0108 D10, Gate 5 M1) — `discard()`, NOT `clear()`. This is an
    // ABANDONMENT and it is the reason this function is named `…MaybeDiscard`: the
    // last line has just gone to the CART, so this RFQ will never be submitted,
    // while ADR-0082's eager create already minted a server draft for it on the
    // 0→1 parts transition. `clear()` here reset `draftRfqId` locally and left
    // that row orphaned — precisely the defect UID-312 exists to close. Do NOT
    // confuse this with submit success below (the one genuine `clear()` site):
    // there the RFQ has become a real customer request and abandoning it would
    // tombstone it. Both callers of this helper are abandonments — moving lines
    // to the cart is not a submit and not a server-side conversion; nothing
    // downstream reads this draft again.
    const emptiedDraft = ids.length > 0 && ids.length === lines.length
    if (emptiedDraft) {
      rfqBundle.discard()
    }
    // ONE navigate call site (see doc comment). `navigateState` is passed
    // through on BOTH branches, so the generate-quote intent survives whether
    // the draft emptied (all-catalog) or lines were left behind (mixed).
    if (emptiedDraft || opts?.alwaysNavigate) {
      navigate('/cart', opts?.navigateState ? { state: opts.navigateState } : undefined)
    }
  }

  /** UID-263 (ADR-0087 D2=FULL) per-row realignment — `PartsReviewTable`
   * reports a successful catalog add; this silently removes that ONE line
   * from the RFQ (no confirm modal on this path — matches `.dc.html`'s
   * `composeAddPart`, unlike bulk) and discards+navigates if it was the
   * last one. */
  function handleLineAddedToCart(id: string) {
    removeLinesFromRfqAndMaybeDiscard([id])
  }

  /** UID-263 (ADR-0087 D3) — the shared batch executor: fires the
   * cart-eligible set via `addLinesToCart`'s ONE `POST /cart/lines/add-batch`
   * call (UID-616 — was N concurrent single-line POSTs), removes only the
   * successfully-added lines (partial-failure honesty — never removes a line
   * whose add failed), announces a truthful summary, and discards+navigates
   * when the draft empties. Double-submit guarded — early-returns if a batch
   * is already running (ADR-0087 D3). */
  async function runBulkAdd(opts?: {
    readonly alwaysNavigate?: boolean
    readonly navigateState?: unknown
  }) {
    if (bulkAdding) return
    setBulkAdding(true)
    try {
      const targets = cartEligibleLines(lines)
      // UID-616 W1 — the batch endpoint caps at `CART_ADD_BATCH_MAX_LINES`
      // (`AddCartLinesRequest.maxItems`); without this pre-flight check an
      // over-cap bulk add would 422 with every line unactionably "failed."
      // Mirrors `RfqDetailSummaryRail.handleCheckout`'s identical guard.
      if (targets.length > CART_ADD_BATCH_MAX_LINES) {
        showToast(
          `This RFQ has ${targets.length} priced lines, over the ${CART_ADD_BATCH_MAX_LINES}-line cart limit. Contact support to complete this order.`,
          'error',
        )
        return
      }
      const results = await addLinesToCart(targets)
      const summary = summarizeCartAddBatch(results)
      removeLinesFromRfqAndMaybeDiscard(summary.addedLineIds, opts)
      showToast(summary.message, summary.tone)
      setBulkAnnouncement((current) => ({ text: summary.message, nonce: current.nonce + 1 }))
    } finally {
      setBulkAdding(false)
    }
  }

  /** UID-263 (ADR-0087 D5) — the bulk button's click handler: skips straight
   * to the batch add when `ff_hide_bulk_cart_warn` suppresses the modal
   * (fail-open on a throwing `localStorage`, so a throw here always means
   * "show the modal," never "skip it"); otherwise opens the confirm modal. */
  function handleAddBulkClick() {
    if (bulkAdding) return
    if (isBulkCartWarnSuppressed()) {
      void runBulkAdd()
      return
    }
    setBulkConfirmIntent('plain')
  }

  /** UID-401 D9 — the Checkout CTA (and its "Generate quote" sibling below)
   * reuse the FULL existing bulk-add mechanism: add the cart-eligible lines,
   * then apply ADR-0087 D2's realignment (remove them from this draft, and
   * `discard()` the draft if that empties it). That realignment is NOT
   * optional and must not be "improved" away: leaving a line in the cart AND
   * on a live RFQ risks the customer being quoted and charged for the same
   * part twice, which is precisely why D2 chose FULL realignment.
   *
   * Because that side effect can DISCARD the user's draft, these CTAs ALWAYS
   * route through `BulkAddPricedConfirmModal` and are deliberately NOT
   * skippable via `ff_hide_bulk_cart_warn` (unlike the `'plain'` button,
   * ADR-0087 D5). That flag was granted for a button whose label already says
   * "Add N priced to cart"; "Checkout" does not advertise that it may delete
   * the request you are composing, so the confirmation is the only thing
   * making the discard explicit rather than silent. */
  function handleCheckoutClick() {
    if (bulkAdding) return
    setBulkConfirmIntent('checkout')
  }

  /** UID-401 D8/D9 — same flow as Checkout, plus the one-shot cart-arrival
   * intent that UID-403 reads to auto-open the shared Generate-quote modal. */
  function handleGenerateQuoteClick() {
    if (bulkAdding) return
    setBulkConfirmIntent('generate-quote')
  }

  function handleBulkConfirmCancel() {
    setBulkConfirmIntent(null)
  }

  /** UID-263 (ADR-0087 D5) — "don't show this again" is written ONLY on
   * confirm, ONLY when checked (never on cancel, never merely on toggling
   * without confirming).
   *
   * UID-401 D9 — which flow runs is read from `bulkConfirmIntent`, captured
   * BEFORE it is cleared, so the confirm that fires can never disagree with
   * the CTA the user actually clicked. The two cart-bound CTAs additionally
   * force navigation (`alwaysNavigate`) because they must land on `/cart`
   * even when cart-ineligible lines are left behind on a still-live draft.
   *
   * Gate-5 W4 fix (UID-401) — `suppressBulkCartWarn()` fires ONLY for the
   * `'plain'` intent (the original "Add N priced to cart" button, the ONE
   * flow ADR-0087 D5 granted `ff_hide_bulk_cart_warn` to). Before this fix,
   * `hideNext` alone gated the call regardless of which CTA opened the
   * modal — so ticking "don't show this again" inside the Checkout/
   * "Generate quote" confirm (which `BulkAddPricedConfirmModal` never even
   * renders the checkbox for, but a stale/future caller could still pass
   * `hideNext: true`) would silently disable the warning for the UNRELATED
   * plain bulk-add path too, which ADR-0125 D9 requires to stay skippable
   * only via its own explicit opt-in. Belt-and-braces with that modal's own
   * `!isCartBoundFlow` checkbox guard — either alone closes the leak, both
   * are kept because the failure is silent and surfaces far from its cause. */
  function handleBulkConfirm(hideNext: boolean) {
    const intent = bulkConfirmIntent
    setBulkConfirmIntent(null)
    if (shouldSuppressBulkCartWarn(intent, hideNext)) suppressBulkCartWarn()
    if (intent === 'checkout') {
      void runBulkAdd({ alwaysNavigate: true })
      return
    }
    if (intent === 'generate-quote') {
      void runBulkAdd({
        alwaysNavigate: true,
        navigateState: GENERATE_QUOTE_CART_INTENT_STATE,
      })
      return
    }
    void runBulkAdd()
  }

  function handleNeedByDateChange(value: string) {
    rfqBundle.setDraft({ needByDate: value })
  }

  function handleRegionChange(value: RfqRegion) {
    rfqBundle.setDraft({ region: value })
  }

  function handleNotesChange(value: string) {
    rfqBundle.setDraft({ notes: value })
  }

  /** UID-401 (ADR-0125 D5) — the compose rail's Shipping/Tax estimate is a
   * projection of the PERSISTED draft (`RfqView.costEstimate`), not a new
   * standalone endpoint (D5 §8 — "not a new seam"). So any picker that can
   * move the figure — the ship method (keys shipping) or the ship-to
   * address (keys tax) — must PATCH-sync the draft immediately, the same
   * `buildUpdateRequest` shape Share/Submit already send, and read the
   * refreshed `costEstimate` back off the response. `overrides` lets a
   * caller pass the value it is ABOUT to set (the `rfqBundle.setDraft` call
   * next to each call site), since the closed-over `shipMethod`/
   * `shipToAddressId`/`shippingAccountId` are still last render's value at
   * the moment this fires. A missing `draftRfqId` (no lines yet) is a
   * defensive no-op — there is nothing to sync.
   *
   * Deliberately fire-and-forget from each caller (`void`): this is a
   * pre-checkout estimate refresh, not a blocking gate on the picker itself
   * — the select's own value updates immediately via `rfqBundle.setDraft`
   * regardless of how this round trip resolves, and a failure here simply
   * leaves the rail showing its last-known estimate rather than surfacing a
   * new error UI for a non-binding figure. */
  async function syncCostEstimate(overrides: {
    shipMethod?: RfqShipMethod | 'none'
    shippingAccountId?: string
    shipToAddressId?: string
  }) {
    const rfqId = draftRfqId
    if (!rfqId) return
    const patched = await apiClient.PATCH('/rfq/{rfqId}', {
      params: { path: { rfqId } },
      body: buildUpdateRequest({
        lines,
        needByDate,
        region,
        notes,
        services,
        noServicesAck,
        shipMethod: overrides.shipMethod ?? shipMethod,
        shippingAccountId: overrides.shippingAccountId ?? shippingAccountId,
        shipToAddressId: overrides.shipToAddressId ?? shipToAddressId,
      }),
    })
    if (patched.data) {
      recordCostEstimate(patched.data)
      // UID-530 AC3 — a full-draft PATCH-sync response is authoritative for
      // every line on the server draft, so it is another honest harvest point.
      recordSpecSelections(patched.data)
    }
  }

  function handleShipMethodChange(value: RfqShipMethod | 'none') {
    // S1 (code-review): a stale "select a shipping account" error from a prior failed
    // submit must not survive a ship-method change — whether the user is moving AWAY from
    // own_account (the picker, and its error, are about to disappear) or back TO it (the
    // error hasn't been re-validated). Only submit should ever set this error again.
    setShipAccountError(undefined)
    // UID-201 (ADR-0076): switching TO own_account with nothing selected yet pre-selects the
    // book's default account (checkout's default-on-select parity) so an own-account draft
    // has a resolvable reference without an extra click. Empty book → nothing to default.
    if (value === 'own_account' && shippingAccountId === '') {
      const fallback = defaultShippingAccountId(shippingAccounts)
      rfqBundle.setDraft({
        shipMethod: value,
        ...(fallback ? { shippingAccountId: fallback } : {}),
      })
      void syncCostEstimate({
        shipMethod: value,
        ...(fallback ? { shippingAccountId: fallback } : {}),
      })
      return
    }
    rfqBundle.setDraft({ shipMethod: value })
    void syncCostEstimate({ shipMethod: value })
  }

  // UID-201 (ADR-0076): the own-account picker's selection — the id sent as
  // `shippingAccountId` (resolved server-side). Clears any stale validation error.
  function handleSelectShippingAccount(id: string) {
    setShipAccountError(undefined)
    rfqBundle.setDraft({ shippingAccountId: id })
    // UID-401 — own_account never carries a shipping FIGURE (D5 #3), but the
    // account choice can still affect the response shape going forward, and
    // keeping this in lock-step with the other two pickers is simpler than
    // special-casing it out.
    void syncCostEstimate({ shippingAccountId: id })
  }

  function handleShipToAddressIdChange(value: string) {
    rfqBundle.setDraft({ shipToAddressId: value })
    // UID-401 (ADR-0125 D5) — the ship-to address is what keys Tax
    // (`salesTaxRatePct`), so this picker must sync too, exactly like
    // `handleShipMethodChange` above.
    void syncCostEstimate({ shipToAddressId: value })
  }

  // UID-201 (ADR-0076 Decision 4) — "＋ Add new shipping account": POST the new account to
  // the shared book, refresh the list, and auto-select the returned id (create-then-select,
  // exactly as checkout does). Rethrow on failure so AddShippingAccountModal keeps itself open.
  async function handleAddShippingAccount(
    fields: Parameters<typeof toCreateShippingAccountRequest>[0],
  ) {
    const { data, error } = await apiClient.POST('/account/shipping-accounts', {
      body: toCreateShippingAccountRequest(fields),
    })
    if (data) {
      setShippingAccounts(data.shippingAccounts)
      setShipAccountError(undefined)
      rfqBundle.setDraft({ shippingAccountId: data.shippingAccountId })
      return
    }
    if (error?.status === 401) return // Handled globally.
    throw new Error('createShippingAccount failed')
  }

  // UID-201 (ADR-0076 Decision 4) — "＋ Add new address…": reuse AddressFormModal, POST to the
  // shared account-wide address book, refresh the list, and auto-select the returned id
  // (create-then-select, exactly as checkout's ShippingAddressStep does).
  async function handleAddAddress(
    fields: Parameters<typeof toCreateAddressRequest>[0],
    isDefault = false,
  ) {
    const { data, error } = await apiClient.POST('/account/addresses', {
      body: toCreateAddressRequest(fields, isDefault),
    })
    if (data) {
      setAddresses(data.addresses)
      rfqBundle.setDraft({ shipToAddressId: data.addressId })
      return
    }
    if (error?.status === 401) return // Handled globally.
    throw new Error('createAddress failed')
  }

  function handleAddService(service: RfqReviewService) {
    rfqBundle.setDraft({ services: [...services, service] })
  }

  function handleRemoveService(localId: string) {
    rfqBundle.setDraft({
      services: services.filter((service) => service.localId !== localId),
    })
  }

  // UID-346 (ADR-0117 D6b/D9) — the Bulk configure modal's Save. Replaces the
  // UID-260/ADR-0085 stub, which only ever wrote `SpecSelection.attributes`
  // straight into the local bundle with NO server round trip. This is now a
  // REAL, set-based write: `POST /rfq/{rfqId}/lines/bulk-configure`, called
  // ONCE for the whole target set (never a loop, D9 — the per-row Configure
  // button and the bulk "Configure N" action are the SAME call at different
  // `lineIds` sizes).
  //
  // Code review (2026-08-27, W6): a leading full-array `PATCH /rfq/{rfqId}`
  // re-sync used to run here before the set-based call — reintroducing the
  // unguarded-lost-update shape (a full-array replace with no version guard
  // on `lines`) that D6b's dedicated, set-based endpoint exists to eliminate.
  // Removed, and NEVER restored (ADR-0117 D11 below reaffirms this): the
  // set-based `bulk-configure` call below is the only WRITE of the RFQ's
  // configuration on this path (besides the create-fallback, which is
  // unchanged, and the seam call just above it, which only ever appends).
  //
  // `draftRfqId` is expected to already exist by the time Configure is
  // reachable at all (the eager 0→1 create effect above mints it as soon as
  // there is a first line) — the `if (!rfqId)` branch below is the same
  // defensive no-op posture `handleShare` takes for the same theoretical
  // race, not an expected path.
  //
  // ADR-0117 D11 (2026-08-27) — a line added to the LOCAL bundle after the
  // eager 0→1 create (a 2nd/3rd/... part, which mints no server sync of its
  // own — only Share/Submit PATCH-sync the full draft) does not yet exist as
  // a line on the server draft. This was FLAGGED as a residual gap by the
  // frontend engineer and CONFIRMED by the architect as a REGRESSION against
  // `main` (where Configure worked for every line regardless of server sync
  // state), not a deferrable pre-existing wart — the ticket's own headline
  // N>1 flow (AC1), broken in the ORDINARY case of adding 2-3 parts and
  // Configuring any one but the first. Fixed by calling the shared, named
  // `ensureLinesOnServerDraft` seam (`ensure-lines-on-server-draft.ts`)
  // immediately before `bulk-configure` — see that module's doc comment for
  // the full ruling (why append-not-PATCH, why lazy-at-Configure-time IS the
  // durable end state and not a narrower stand-in for an eager one, and the
  // 409-reconcile behavior). D11 is a STANDING RULE: any future server-backed
  // compose-time operation must call this same seam, never re-derive it
  // inline here or anywhere else.
  /**
   * THE one "mint a server draft from the current compose state" path, shared
   * by Configure's no-id-yet branch, Submit's create-at-submit fallback
   * (ADR-0082), and — new — the stale-`draftRfqId` recovery both of them now
   * run (`stale-draft-recovery.ts`).
   *
   * Extracted rather than copied for the reason `ensureLinesOnServerDraft`
   * exists as a named seam: there are now THREE callers of "create the draft
   * and record everything the response tells us", and a fourth that forgot one
   * of those records (`rfqNumber`, `costEstimate`, the line set, the library
   * spec snapshot) would be a silent, screen-level inconsistency.
   *
   * DISCARDS `draftRfqId` FIRST, before the request. If the caller is here
   * because the current id is stale, that id must not survive a failed create
   * — it is persisted (ADR-0103 D1), so leaving it in place is what turns one
   * bad id into a permanent dead end (the defect being fixed). Losing it costs
   * nothing that isn't already lost: it names a row the server does not have.
   *
   * `creatingDraft` is held for the duration so the eager 0→1 effect cannot
   * mint a SECOND draft while this one is in flight (its guard reads that
   * flag) — the same best-effort race guard ADR-0082 §3 describes.
   *
   * `slotId` — UID-552 (ADR-0170) ASYNC RACE FIX: the caller must capture its
   * active slot id BEFORE its own first `await` and pass that value here, not
   * read `activeSlotId` at call time. This function itself awaits (the
   * `POST /rfq` below), and — via `withFreshDraftRetry` — may be invoked a
   * second time after an even earlier `await` from a still-in-flight retry.
   * Either await gives a slot switch a window to land the fresh draft id on
   * whatever slot happens to be active when the network call resolves, which
   * is exactly the race this branch spent 3 review rounds eliminating. Using
   * the caller's captured `slotId` instead means a switch mid-flight writes
   * to an already-discarded slot — a harmless no-op — rather than corrupting
   * the newly-active one. `undefined` (the slot having been discarded before
   * the call was even made) is likewise a no-op.
   */
  async function createDraftFromCurrentLines(
    slotId: string | undefined,
    failureMessage: string,
  ): Promise<FreshDraft & { readonly serverLineIds: ReadonlySet<string> }> {
    if (slotId !== undefined) {
      rfqBundle.setSlotRfqId(slotId, undefined)
      rfqBundle.setSlotRfqNumber(slotId, undefined)
      rfqBundle.setSlotCreating(slotId, true)
    }
    try {
      const created = await apiClient.POST('/rfq', {
        body: buildCreateRequest({
          lines,
          needByDate,
          region,
          notes,
          services,
          noServicesAck,
          shipMethod,
          shippingAccountId,
          shipToAddressId,
        }),
      })
      if (!created.data) {
        return {
          ok: false,
          message: created.error?.detail ?? failureMessage,
          serverLineIds: new Set(),
        }
      }
      if (slotId !== undefined) {
        rfqBundle.setSlotRfqId(slotId, created.data.id)
        rfqBundle.setSlotRfqNumber(slotId, created.data.rfqNumber ?? undefined)
      }
      recordServerLines(created.data)
      recordCostEstimate(created.data)
      recordSpecSelections(created.data)
      return {
        ok: true,
        rfqId: created.data.id,
        // Handed back as a VALUE, not just pushed into state: a caller already
        // mid-flight cannot observe `setServerLineIds` (see
        // `handleSaveConfigure`'s `knownServerLineIds`).
        serverLineIds: serverLineIdsOf(created.data),
      }
    } finally {
      if (slotId !== undefined) rfqBundle.setSlotCreating(slotId, false)
    }
  }

  async function handleSaveConfigure(
    lineIds: readonly string[],
    specification: RfqLineSpecification,
    quantity: number | null,
  ): Promise<{ readonly ok: true } | { readonly ok: false; readonly message: string }> {
    // UID-552 (ADR-0170) ASYNC RACE FIX — same discipline as `handleSubmit`'s
    // create-at-submit fallback: captured before the first `await` in this
    // function, and passed (not `activeSlotId` read later) to every slot-
    // targeted write below. `createFreshDraft` closes over this captured
    // value rather than re-reading `activeSlotId`, because it is ALSO invoked
    // from inside `withFreshDraftRetry` after an await — by which point the
    // active slot may have already changed — which is precisely why the
    // capture (not a live read) is required.
    const capturedSlotId = activeSlotId

    // The seam works from the ids known to be on the server draft. Held as a
    // LOCAL, not read straight off `serverLineIds`, because a recovery mid-call
    // replaces the whole draft: `setServerLineIds` won't be visible to this
    // already-running function (it closes over this render's state), so the
    // retry would reconcile the NEW draft against the DEAD one's line set.
    let knownServerLineIds = serverLineIds

    async function createFreshDraft(): Promise<FreshDraft> {
      const created = await createDraftFromCurrentLines(
        capturedSlotId,
        'Could not save this configuration. Please try again.',
      )
      if (created.ok) {
        knownServerLineIds = created.serverLineIds
      }
      return created
    }

    let rfqId = draftRfqId
    if (!rfqId) {
      // The eager 0→1 create effect hasn't landed a `draftRfqId` yet (still in
      // flight, or it failed and is waiting for Submit's own create-at-submit
      // fallback to retry) — same theoretical race `handleSubmit` already
      // covers with its own create-fallback branch. Configure must not be
      // blocked on that race: create the draft here, right now, from the
      // CURRENT lines, exactly as `handleSubmit`'s fallback does.
      const created = await createFreshDraft()
      if (!created.ok) {
        return { ok: false, message: created.message }
      }
      rfqId = created.rfqId
    }

    /** Reconcile-then-configure against ONE draft id — the whole server-facing
     * body of this handler, as a retryable unit. Both calls in it are
     * `{rfqId}`-scoped, so either can be the one that discovers the draft is
     * gone; `withFreshDraftRetry` below is what turns that into a recovery
     * instead of a dead end. */
    async function configureOn(id: string): Promise<DraftAttempt<RfqView>> {
      const ensured = await ensureLinesOnServerDraft(id, lineIds, lines, knownServerLineIds)
      // UID-530 AC3 — harvest whatever view the seam saw, on BOTH branches. The
      // eager create rides only the lines present at the 0→1 transition, so a
      // 2nd/3rd part's server snapshot first becomes visible to this client on
      // the seam's append response (or its 409 reconcile GET / second append).
      // Persisting it even on failure is the same C3 rule the seam's
      // `serverLineIds` already follows: never discard a round trip's knowledge.
      if (ensured.specSelections) {
        rfqBundle.hydrateSpecSelections(ensured.specSelections)
      }
      if (!ensured.ok) {
        // Code review C3 (2026-08-27) — persist whatever the seam DID learn
        // even on failure (its `serverLineIds` is optional precisely for
        // this branch). Without this, a page remount's empty `serverLineIds`
        // would repeat an identical failing append/reconcile on every
        // subsequent click forever, discarding the network round trip that
        // already resolved most of the picture.
        if (ensured.serverLineIds) {
          knownServerLineIds = ensured.serverLineIds
          setServerLineIds(ensured.serverLineIds)
        }
        return {
          ok: false,
          message: ensured.message,
          ...(ensured.staleDraft ? { staleDraft: true } : {}),
        }
      }
      knownServerLineIds = ensured.serverLineIds
      setServerLineIds(ensured.serverLineIds)

      const result = await apiClient.POST('/rfq/{rfqId}/lines/bulk-configure', {
        params: { path: { rfqId: id } },
        body: {
          lineIds: [...lineIds],
          specification,
          ...(quantity !== null ? { quantity } : {}),
        },
      })
      if (!result.data) {
        return {
          ok: false,
          message: result.error?.detail ?? 'Could not save this configuration. Please try again.',
          ...(isStaleDraftProblem(result.error) ? { staleDraft: true } : {}),
        }
      }
      return { ok: true, value: result.data }
    }

    // A 404 from either call above means the persisted `draftRfqId` no longer
    // names anything (see `stale-draft-recovery.ts` for how that happens and
    // why 404 — and only 404 — licenses this). Discard it, recreate the draft
    // from the current lines, and retry ONCE. Every other failure, including a
    // still-404 retry, is reported to the modal exactly as before.
    const configured = await withFreshDraftRetry(rfqId, configureOn, createFreshDraft)
    if (!configured.ok) {
      return { ok: false, message: configured.message }
    }
    const configuredView = configured.value
    recordServerLines(configuredView)
    recordCostEstimate(configuredView)
    // NOTE: deliberately NOT `recordSpecSelections` here — the merge just below
    // is LAST-wins for exactly the `lineIds` this call configured (it IS the
    // buyer's edit, so it must overwrite), whereas the harvest is first-wins.
    // Using the harvest here would silently refuse to apply the buyer's own
    // Configure result.

    // The server is the source of truth for the resulting `specSelection`
    // (indicativeUnitPrice/pricingOutcome are server-derived, ADR-0117 D7) —
    // merge each configured line's returned selection back into the local
    // draft rather than constructing one client-side.
    const lineIdSet = new Set(lineIds)
    const nextSelections = { ...specSelections }
    for (const returnedLine of configuredView.lines ?? []) {
      const sourceableId = returnedLine.sourceable.id
      if (lineIdSet.has(sourceableId) && returnedLine.specSelection) {
        nextSelections[sourceableId] = returnedLine.specSelection
      }
    }
    let nextQuantityOverrides: Record<string, number> | undefined
    if (quantity !== null) {
      nextQuantityOverrides = { ...quantityOverrides }
      for (const id of lineIds) nextQuantityOverrides[id] = quantity
    }
    const patch: Partial<RfqDraftMeta> = {
      specSelections: nextSelections,
      ...(nextQuantityOverrides ? { quantityOverrides: nextQuantityOverrides } : {}),
    }
    rfqBundle.setDraft(patch)
    showToast('Configuration saved.', 'success')
    return { ok: true }
  }

  // UID-262 "Move to a new RFQ" — the design source of truth (`.dc.html`
  // L8148-8152, `confirmMoveRfqNew`) is: remove the part from THIS request,
  // create a new RFQ *draft* containing just it (NOT submitted — the earlier
  // UID-114 behavior POSTed `/rfq` then `/submit`, producing a *requested*
  // RFQ, and never routed the user), then navigate to that new draft so the
  // user lands right on it. See ADR-0088 and `buildMoveToNewRfqRequest`.
  //
  // Order matters: create FIRST, and only remove + navigate on success — a
  // failed create leaves the line right where it was, with an error surfaced,
  // rather than silently losing a part the user still expects to see. There
  // is no second (submit) step to fail, so no per-line orphan-draft retry map
  // is needed anymore (removed with UID-262). The success toast (not an
  // aria-live region) is what announces the move, because navigating away
  // unmounts this page — `showToast` lives above the router and survives it.
  async function handleMoveToNewRfq(id: string) {
    const line = lines.find((candidate) => candidate.id === id)
    if (!line) return

    setMoveError(undefined)
    setMovingLineIds((current) => [...current, id])
    try {
      const created = await apiClient.POST('/rfq', { body: buildMoveToNewRfqRequest(line) })
      if (!created.data) {
        setMoveError(
          created.error?.detail ?? `Couldn’t move ${line.name} to a new RFQ. Please try again.`,
        )
        moveAlertRef.current?.focus()
        return
      }

      // Only now that the new draft actually exists: drop the line from this
      // request (ADR-0082 — `removeItem` no longer invalidates this draft's own
      // `draftRfqId`; it stays PATCH-synced at Share/Submit), announce via a
      // navigation-surviving toast, and route to the new draft's review page
      // (`/rfq/{id}` — the same self-fetching deep link "Share for review" and
      // the RFQ list already use; ADR-0088).
      rfqBundle.removeItem(id)
      showToast(`“${line.name}” moved to a new RFQ.`, 'success')
      navigate(`/rfq/${created.data.id}`)
    } finally {
      setMovingLineIds((current) => current.filter((movingId) => movingId !== id))
    }
  }

  function handleNoServicesAckChange(next: boolean) {
    // ADR-0082: `draftRfqId` no longer gets invalidated on edits at all (it's kept valid
    // by PATCH-sync at Share/Submit, not by discard-on-edit) — this comment used to
    // explain why toggling this ack specifically was exempt from that; the exemption
    // itself is now moot, but the ack still never reaches the wire
    // (`buildCreateRequest`/`buildUpdateRequest` both drop it).
    setNoServicesAck(next)
  }

  async function handleSubmit() {
    // UID-552 (ADR-0170) ASYNC RACE FIX — captured BEFORE the first `await`
    // below, exactly like the eager-create effect. Every write this handler
    // makes after an `await` targets this captured id (`setSlotRfqId`, then
    // `clearSlot` on success) rather than "whichever slot happens to be
    // active when the network call resolves" — a user who navigates away and
    // begins a different draft mid-submit must not have this RFQ's server id
    // (or its post-submit local reset) land on that other draft.
    const capturedSlotId = activeSlotId
    setSubmitError(undefined)

    const dateError = validateNeedByDate(needByDate, todayIsoDate())
    setNeedByDateError(dateError)
    if (dateError) {
      needByDateInputRef.current?.focus()
      return
    }

    // UID-201 (ADR-0076): mirrors the need-by-date guard above — own_account without a
    // SELECTED shipping account is a client-side-catchable rejection the server's
    // resolve-on-write + IDOR gate would otherwise 422 (review-domain's validateShipping).
    const shipError = validateShipping(shipMethod, shippingAccountId)
    setShipAccountError(shipError)
    if (shipError) {
      // The error <p> mounts on the NEXT render (it's conditional on shipAccountError), so a
      // synchronous focus here would hit a not-yet-attached ref — the focus is driven by the
      // effect keyed on `shipAccountError` instead.
      return
    }

    // Belt-and-braces alongside the summary rail's disabled Submit button
    // (the primary gate, screens.md §15): re-check the full submit-readiness
    // rule here too, in case this ever gets invoked from a path that
    // bypassed the disabled button. Need-by date and services are optional
    // and no longer part of this gate — the two remaining checks are "at
    // least one part" (`canSubmitRfq`) and part-configuration completeness
    // (`needsConfigCount`), so a submit can never succeed with an
    // unconfigured part even if some other path skips the disabled CTA.
    if (!canSubmitRfq({ partsCount: summary.partsCount }) || needsConfigCount > 0) {
      return
    }

    setSubmitting(true)
    try {
      // ADR-0082 (UID-242 Item 1): the common path is now "the eager 0→1 effect above
      // already minted `draftRfqId`" — Submit just syncs its latest content
      // (`PATCH /rfq/{rfqId}`) and submits. The literal create-then-submit two-call
      // sequence is kept ONLY as the fallback for when no draft was eagerly created
      // (the effect hasn't resolved yet, or its `POST /rfq` failed) — exactly the same
      // two calls this page always made, just demoted to a fallback rather than the
      // only path.
      let rfqId = draftRfqId
      if (rfqId) {
        const patched = await apiClient.PATCH('/rfq/{rfqId}', {
          params: { path: { rfqId } },
          body: buildUpdateRequest({
            lines,
            needByDate,
            region,
            notes,
            services,
            noServicesAck,
            shipMethod,
            shippingAccountId,
            shipToAddressId,
          }),
        })
        if (patched.data) {
          recordCostEstimate(patched.data)
        } else if (isStaleDraftProblem(patched.error)) {
          // The persisted `draftRfqId` no longer names anything (see
          // `stale-draft-recovery.ts`). Submit is not a dead end for that: it
          // already owns a create-from-current-state path — the `else` branch
          // below — and this sync exists only to push that same state onto an
          // EXISTING draft. So fall through to it. No `withFreshDraftRetry`
          // here, deliberately: falling through IS the single retry, and the
          // fresh draft already carries everything the PATCH would have sent,
          // so re-running the sync against it would be a no-op round trip.
          rfqId = undefined
        } else {
          setSubmitError(patched.error?.detail ?? 'Could not update this RFQ. Please try again.')
          alertRef.current?.focus()
          return
        }
      }
      if (!rfqId) {
        const created = await createDraftFromCurrentLines(
          capturedSlotId,
          'Could not create this RFQ. Please try again.',
        )
        if (!created.ok) {
          setSubmitError(created.message)
          alertRef.current?.focus()
          return
        }
        rfqId = created.rfqId
      }

      const submitted = await apiClient.POST('/rfq/{rfqId}/submit', {
        params: { path: { rfqId } },
      })
      if (!submitted.data) {
        setSubmitError(submitted.error?.detail ?? 'Could not submit this RFQ. Please try again.')
        alertRef.current?.focus()
        return
      }

      // UID-586 — the request has actually been submitted now, but this page
      // STAYS mounted (the design's in-page banner, never a navigation) and
      // keeps showing this slot's lines, so the slot itself is NOT cleared
      // here anymore. `submittedSlotIdToClearRef` defers that clear to this
      // page's own unmount (see the effect above) — still exactly once,
      // still before any OTHER "Add to RFQ" could resurrect it (UID-119's
      // original concern), because leaving this page is a precondition for
      // starting a new draft elsewhere.
      submittedSlotIdToClearRef.current = capturedSlotId
      setPostSubmitRfq(submitted.data)
      setPostSubmitAt(Date.now())
    } finally {
      setSubmitting(false)
    }
  }

  // ADR-0082 (UID-242 Item 1), as amended by UID-466/ADR-0152 — "Share for review".
  // Still syncs the server draft (`PATCH /rfq/{rfqId}`) FIRST, so the link a teammate
  // opens reflects the current bundle rather than whatever the eager-create effect wrote
  // at 0→1 (save-then-copy snapshot, ADR-0082 §6 — re-opening Share re-syncs). What
  // CHANGED in UID-466: on success this opens `RfqShareModal` (the design's
  // "Share · this RFQ" dialog, `dc.html:4824-4854`) instead of copying silently. The
  // clipboard write itself is unchanged and now lives in `handleCopyShareLink` below,
  // which the modal's "Copy link" button calls — so the PATCH-then-copy ORDER still
  // holds: nothing can be copied until this sync has succeeded and opened the dialog.
  // The failure paths are untouched: a failed PATCH toasts and opens nothing.
  async function handleShare() {
    const rfqId = draftRfqId
    if (!rfqId) return // Button is disabled without one; defensive no-op if invoked anyway.

    setSharing(true)
    try {
      const patched = await apiClient.PATCH('/rfq/{rfqId}', {
        params: { path: { rfqId } },
        body: buildUpdateRequest({
          lines,
          needByDate,
          region,
          notes,
          services,
          noServicesAck,
          shipMethod,
          shippingAccountId,
          shipToAddressId,
        }),
      })
      if (!patched.data) {
        showToast(patched.error?.detail ?? "Couldn't sync this RFQ. Please try again.", 'error')
        return
      }
      recordCostEstimate(patched.data)

      setShareOpen(true)
    } finally {
      setSharing(false)
    }
  }

  /** UID-466 — the share modal's "Copy link" action. Unchanged ADR-0082 §6
   * behavior (the internal, tenant-scoped `/rfq/{draftRfqId}` deep link, the same
   * `navigator.clipboard.writeText` + success/error `showToast` pattern
   * `RfqShareQuoteModal.handleCopyLink` carries) — only its trigger moved, from the
   * rail button to inside the dialog. No PATCH here: `handleShare` has already
   * synced the draft as the precondition for this dialog being open at all.
   *
   * The failure copy no longer says "copy it manually from your address bar": with
   * the dialog open the address bar still reads `/rfq/new`, so that instruction
   * would have pointed the buyer at the wrong URL. It names the link instead.
   *
   * RETURNS whether the clipboard write actually succeeded (Gate-5 C2). The modal
   * needs that answer to decide whether to relabel its button to "Link copied";
   * when this was `Promise<void>` and the caller discarded it, the label asserted
   * a success that had not happened and held it for the dialog's life. */
  async function handleCopyShareLink(): Promise<boolean> {
    const rfqId = draftRfqId
    if (!rfqId) return false // Unreachable: the modal only opens after a successful sync.

    const link = `${window.location.origin}/rfq/${rfqId}`
    try {
      await navigator.clipboard.writeText(link)
      showToast('Link copied to clipboard.', 'success')
      return true
    } catch {
      showToast(`Couldn't copy automatically — the link is ${link}`, 'error')
      return false
    }
  }

  /** UID-466 (ADR-0152) — DISPLAY-ONLY, and recorded as such. Mirrors the design
   * prototype's own `shareInvite()` (`dc.html:7354`), which likewise only flashes a
   * message: no share ACL is created, because none exists to create — `SharedSession`
   * is a single opaque link token with one `openedByUserId`, not a per-recipient
   * grant. The copied link (above) remains the only real capability this dialog has.
   * See `RfqShareModal`'s and `share-people.ts`'s doc comments. */
  function handleShareWith(personIds: readonly string[]) {
    const count = personIds.length
    // Gate-5 C1 — this used to raise a GREEN SUCCESS toast reading "Shared with N
    // people.", after a dialog of named colleagues with job titles. Nothing was
    // shared, and a buyer waiting on a review would have waited forever. ADR-0152
    // D3's own binding sentence is that this dialog "must not be presented as a
    // working collaboration feature", and that toast presented it as exactly one.
    // Tone `info`, and the copy states what did and did not happen.
    showToast(
      `Noted ${count} ${count === 1 ? 'teammate' : 'teammates'} — no one was notified. Use Copy link to share this RFQ.`,
      'info',
    )
  }

  /** UID-333 F3 (ADR-0116) — the design's `< Back` control. Mirrors
   * `RfqDetailPage.handleBack` rather than inventing a second pattern:
   * `location.key === 'default'` means this entry has NO predecessor in this
   * session's history stack (a fresh tab, a bookmark, a shared deep link),
   * where `navigate(-1)` would leave the app entirely — so that case falls
   * back to the RFQ list. */
  function handleBack() {
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate('/rfq')
    }
  }

  /** UID-611 review round (finding 9, ADR-0179 D3) — this screen's own
   * render site for `RfqCard`'s `onRfqDeleted` prop, mirroring
   * `RfqDetailPage.handleRfqDeleted` (the ONLY site that was wired before
   * this fix): a line removal that took the RFQ's last remaining line
   * cascade-deletes the RFQ server-side, and `RfqCard` has no route
   * knowledge of its own to navigate away with. Below, `<RfqCard>` mounts
   * here TOO (the post-submit "reuse the real detail card" section, per that
   * render site's own doc comment) — leaving `onRfqDeleted` unset there
   * meant a cascade delete triggered FROM THIS PAGE silently no-opped
   * (`onRfqDeleted?.()` on `undefined`), stranding the buyer on a page still
   * rendering a now-deleted RFQ's `liveRfq` poll. `mountedRef`-guarded so a
   * stale poll response landing after the buyer has already navigated away
   * by some other route never fires a second, unwanted navigation.
   *
   * A plain function, not `useCallback` — same as `handleBack` just above
   * (and every other handler in this render region, which sits AFTER this
   * component's `auth.status` early-return guards). A hook call here would
   * be a real Rules-of-Hooks violation: on the renders that bail out at
   * those guards, it would simply never run. */
  function handleRfqCardDeleted() {
    if (!mountedRef.current) return
    navigate('/rfq')
  }

  return (
    <section aria-labelledby="rfq-review-heading" className="rfq-review-page">
      {/* UID-261 — mirrors `.dc.html`'s placement (the analysis overlay is
       * the FIRST thing inside the review screen's root, `sc-if
       * rfqAnalyzing` right after `<main>` opens, L4072). Resolves into the
       * rest of this page once every part has settled. */}
      {rfqAnalysis.active && (
        <RfqAnalysisOverlay
          rows={rfqAnalysis.rows}
          total={rfqAnalysis.total}
          doneN={rfqAnalysis.doneN}
          progressPct={rfqAnalysis.progressPct}
          moreAbove={rfqAnalysis.moreAbove}
          moreBelow={rfqAnalysis.moreBelow}
        />
      )}
      <button type="button" className="back-link rfq-review-back-link" onClick={handleBack}>
        <LeftOutlined aria-hidden="true" /> Back
      </button>
      {/* UID-309 (`Fictiv Platform.dc.html` ~L4106-4109) — once a draft exists,
       * the reference pairs the same 44x44 lavender document-icon chip the RFQ
       * detail page already ships (ADR-0104 D4, `.rfq-detail-heading-row`/
       * `-icon` — reused verbatim here, same icon/colors) with the real
       * sequential number AND a subtitle recap ("N parts · M units — configure
       * and price each part below..."). `draftRfqId` doesn't exist yet before
       * the 0→1 parts eager-create effect resolves (an empty compose screen has
       * no server draft at all), so the icon+subtitle are held back for that
       * narrow before-any-part-is-added window too — mirroring the heading's
       * own generic-title fallback below. */}
      {draftRfqId ? (
        <div className="rfq-detail-heading-row">
          <span className="rfq-detail-heading-icon" aria-hidden="true">
            <Icon name="document" />
          </span>
          <div>
            <h1 id="rfq-review-heading" className="display-heading">
              {rfqNumberDisplay({ id: draftRfqId, rfqNumber: draftRfqNumber ?? null })}
            </h1>
            <p className="rfq-review-subheading caption-text">
              {lines.length} part{lines.length === 1 ? '' : 's'} ·{' '}
              {lines.reduce((sum, line) => sum + line.quantity, 0)} units — configure and price each
              part below. Add priced parts to your cart, and send the rest to sourcing as one quote
              package.
            </p>
          </div>
        </div>
      ) : (
        <h1 id="rfq-review-heading" className="display-heading">
          Build your RFQ request
        </h1>
      )}
      <p aria-live="polite" className="sr-only">
        {submitting ? 'Submitting your RFQ request.' : ''}
      </p>
      {/* UID-262: a successful "Move to a new RFQ" navigates away immediately,
       * so its success is announced by a navigation-surviving toast (see
       * handleMoveToNewRfq) rather than an aria-live region on this unmounting
       * page. A create FAILURE keeps the user here — surfaced by `moveError`
       * below. */}

      {submitError && (
        <FormAlert ref={alertRef} title="Couldn’t submit this RFQ" detail={submitError} />
      )}

      {moveError && (
        <FormAlert ref={moveAlertRef} title="Couldn’t move this part" detail={moveError} />
      )}

      {/* UID-260 gap #2 (frontend-only follow-up) — the PREVIEW-ONLY "Choose a
       * quote option" selector, rendered ONLY while at least one line is a
       * real gap #1 candidate. See `RfqPreQuotePricingPreview.tsx`'s doc
       * comment for the full preview-only caveat this ticket requires.
       *
       * UID-586 — gated on NOT YET quoted (draft OR pending), not on
       * draft-only: `RfqCard`'s OWN "Choose a quote option" card (real
       * pricing) only appears once `showsQuote` (`status === 'quoted'`,
       * mirroring the summary-rail swap's own corrected condition) — while
       * merely pending there is nothing yet to duplicate. */}
      {liveRfq?.status !== 'quoted' && (
        <RfqPreQuotePricingPreview
          lines={lines}
          selectedVariantId={previewVariantId}
          onSelect={setPreviewVariantId}
        />
      )}

      <div className="rfq-review-layout">
        <div className="rfq-review-main">
          {/* UID-333 F1 (ADR-0116) — the design's details card is LATER in
           * source but carries `order:-1` (`dc.html:4245`), so it renders
           * FIRST in the flex column. Reordered in the DOM rather than with
           * CSS `order`: a CSS-only reorder would match the pixels and leave
           * screen-reader and keyboard-tab order on the old sequence.
           *
           * UID-586 correction — visible for draft AND pending (confirmed
           * against a real design screenshot: "Request details & services"
           * is present, collapsed, alongside the PENDING amber banner), but
           * hidden once quoted (the green "returned" banner's screen never
           * carries this card) — the same not-yet-quoted condition as the
           * `RfqPreQuotePricingPreview` gate below. */}
          {liveRfq?.status !== 'quoted' && (
            <RequestDetailsSection
              needByDate={needByDate}
              onNeedByDateChange={handleNeedByDateChange}
              needByDateError={needByDateError}
              needByDateInputRef={needByDateInputRef}
              region={region}
              onRegionChange={handleRegionChange}
              notes={notes}
              onNotesChange={handleNotesChange}
              lines={lines}
              services={services}
              onAdd={handleAddService}
              onRemove={handleRemoveService}
              noServicesAck={noServicesAck}
              onNoServicesAckChange={handleNoServicesAckChange}
            />
          )}

          {/* UID-586 — once `handleSubmit` has actually succeeded, this whole
           * section REUSES the RFQ *detail* page's own `RfqCard` (real
           * per-line pricing/images, its own status treatment, and the
           * quoted-lines region/variant picker) instead of recreating any
           * piece of that UI here. `PartsReviewTable`/`RequestSummaryRail`
           * below are the DRAFT-only rendering — they have no path to a real
           * server-priced line at all, so trying to merge real prices into
           * them would be re-deriving what `RfqCard` already does
           * correctly. */}
          {liveRfq !== undefined ? (
            <RfqCard
              rfq={liveRfq}
              onChanged={() => void refetchPostSubmitRfq()}
              onRfqDeleted={handleRfqCardDeleted}
              selectedVariantId={selectedVariantId}
              onVariantSelect={setSelectedVariantId}
              // UID-586 — the design's own exact banner copy
              // (`Fictiv Platform.dc.html` ~L4137-4151), passed through to
              // `RfqCard`'s "Parts in this request" card, which renders it
              // between the header bar and the table — exactly where the
              // design puts it. Scoped to THIS compose flow only: `RfqCard`
              // defaults `submissionBanner` to nothing, so the RFQ detail
              // page/list (which also mounts `RfqCard`) never shows it.
              // Driven by the exact same `liveRfq` prop `RfqCard` itself
              // renders from, so the two can never show out-of-sync states —
              // one poll, one source of truth.
              submissionBanner={
                postSubmitAt !== undefined ? (
                  <RfqSubmissionStatusBanner status={liveRfq.status} submittedAt={postSubmitAt} />
                ) : undefined
              }
            />
          ) : (
            <Card aria-labelledby="rfq-parts-heading" className="rfq-parts-card">
              <div className="rfq-parts-card-header">
                {/* UID-309 — design-literal heading from the source of truth
                 * (`Fictiv Platform.dc.html:4131`, "Parts in this request").
                 * Deliberately the SAME string the RFQ *detail* screen already
                 * ships (ADR-0092/UID-267, `RfqCard.test.tsx:2504`), so the
                 * compose and detail surfaces name this section identically. */}
                <h2 id="rfq-parts-heading" className="heading-text">
                  Parts in this request
                </h2>
                {/* UID-309 (`Fictiv Platform.dc.html` ~L4131) — the reference
                 * puts a `flex:1` spacer between the title and everything
                 * after it, so "Add parts" + the filter chips sit together at
                 * the row's right edge, IN THAT ORDER. This span is that
                 * spacer — no visible content of its own. */}
                <span className="rfq-parts-card-header-spacer" aria-hidden="true" />
                {/* UID-333 F10 (ADR-0116) — the design specifies `height:32px`
                 * with a 14x14 plus SVG for THIS screen's button
                 * (`dc.html:4132`), which is `Button`'s default `md`. UID-309
                 * had pinned `size="sm"` (24px) to match the RFQ *detail*
                 * page's twin button (30px, `dc.html:3561`) — but that is a
                 * different screen with its own spec, and matching it here
                 * traded this screen's fidelity for cross-screen symmetry the
                 * design source does not ask for. */}
                <Button variant="outline" onClick={() => setAddPartsOpen(true)}>
                  <IconPlus size={14} />
                  Add parts
                </Button>
                {/* UID-309 (`Fictiv Platform.dc.html:9675`/`:9657`) — REAL
                 * click-to-filter chips over `filteredLines`, replacing the old
                 * inert "N priced"/"N to configure" `<span>`s. A native
                 * `<fieldset>`/sr-only `<legend>` (rather than `role="group"`
                 * on a `<div>` — biome's a11y rule flags that as reaching for
                 * ARIA where a semantic element already exists) groups the two
                 * `<button>`s; `aria-pressed` exposes each chip's selected
                 * state to assistive tech (WCAG 4.1.2/1.3.1) — a plain
                 * visual-only active class would not. Rendered only once
                 * there is at least one line — an empty request has nothing
                 * to filter. Ordered AFTER "Add parts" (not before), matching
                 * the reference's `title, spacer, Add-parts-button,
                 * partsFilterTabs` order — a prior pass here had these two
                 * swapped. */}
                {lines.length > 0 && (
                  <fieldset className="rfq-parts-filter-chips">
                    <legend className="sr-only">Filter parts</legend>
                    <button
                      type="button"
                      className="rfq-parts-filter-chip"
                      aria-pressed={partsFilter === 'all'}
                      onClick={() => setPartsFilter('all')}
                    >
                      All parts ({lines.length})
                    </button>
                    <button
                      type="button"
                      className="rfq-parts-filter-chip"
                      aria-pressed={partsFilter === 'config'}
                      onClick={() => setPartsFilter('config')}
                    >
                      Needs configuration ({needsConfigCount})
                    </button>
                  </fieldset>
                )}
              </div>
              <PartsReviewTable
                lines={filteredLines}
                onQuantityChange={handleQuantityChange}
                onRemove={handleRemoveLine}
                onMoveToNewRfq={(id) => setPendingMoveLineId(id)}
                movingIds={movingLineIds}
                onConfigure={(ids) => setConfigureTargetIds(ids)}
                onConfigureLine={(id) => setConfigureLineId(id)}
                selectedPreviewVariantId={previewVariantId}
                onAddedToCart={handleLineAddedToCart}
                // UID-464 (ADR-0151 D4) — while the post-rehydrate price
                // re-resolve is in flight, a rehydrated line's Unit-price cell
                // shows a skeleton rather than one of the states that ABSENCE
                // of a price would otherwise mean. Read straight off the bundle
                // (the store that stripped the price and is putting it back),
                // never re-derived here.
                pricesResolving={rfqBundle.pricesResolving}
              />
            </Card>
          )}
        </div>

        <RfqShareModal
          // Gate-5 W4 — remount per open so the dialog can never paint a frame of
          // the PREVIOUS share's selection or its stale "Link copied" label. The
          // modal resets on close as well; this is the belt to that braces.
          key={String(shareOpen)}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          onCopyLink={handleCopyShareLink}
          onShareWith={handleShareWith}
        />

        {/* UID-586 correction — confirmed against a real design screenshot:
         * while merely PENDING (requested/quoting), the design keeps the
         * compose flow's OWN rail (Ship to/Part production/Shipping/Tax/
         * Total), just with a "Quote in progress..." CTA instead of Submit
         * — it does NOT swap to `RfqDetailSummaryRail`'s simpler Active/
         * Requested variant. Only once actually QUOTED does the design
         * switch to the region/variant-aware rail with real pricing. */}
        {liveRfq?.status === 'quoted' ? (
          <RfqDetailSummaryRail
            rfq={liveRfq}
            onChanged={(fresh) => void refetchPostSubmitRfq(fresh)}
            selectedVariantId={selectedVariantId}
            onVariantSelect={setSelectedVariantId}
          />
        ) : (
          <RequestSummaryRail
            shipMethod={shipMethod}
            onShipMethodChange={handleShipMethodChange}
            shippingAccounts={shippingAccounts}
            selectedShippingAccountId={shippingAccountId === '' ? null : shippingAccountId}
            onSelectShippingAccount={handleSelectShippingAccount}
            onAddShippingAccount={() => setShowAddShippingAccount(true)}
            shipAccountError={shipAccountError}
            shipAccountErrorRef={shipAccountErrorRef}
            addresses={addresses}
            shipToAddressId={shipToAddressId}
            onShipToAddressIdChange={handleShipToAddressIdChange}
            onAddNewAddress={() => setShowAddAddress(true)}
            summary={summary}
            submitting={submitting}
            onSubmit={() => void handleSubmit()}
            pending={liveRfq !== undefined}
            shareEnabled={draftRfqId !== undefined}
            sharing={sharing}
            onShare={() => void handleShare()}
            needsConfigCount={needsConfigCount}
            cartEligibleCount={cartEligibleCount}
            showAddBulk={showAddBulk}
            bulkAdding={bulkAdding}
            onAddBulk={handleAddBulkClick}
            showCheckout={showCheckout}
            onCheckout={handleCheckoutClick}
            onGenerateQuote={handleGenerateQuoteClick}
            partProduction={partProductionSubtotal(lines)}
            costEstimate={costEstimate}
            customQuoteCount={needsQuoteLineCount(lines)}
            partProductionCount={partProductionLineCount(lines)}
          />
        )}
      </div>

      {/* UID-263 (ADR-0087 D3) — the bulk batch's truthful outcome summary,
       * announced via its own polite live region (same nonce pattern as
       * `moveAnnouncement` above, ADR-0064 carry-through) so a repeated
       * message (e.g. two consecutive full successes) is still re-announced. */}
      <p aria-live="polite" className="sr-only" key={`bulk-${bulkAnnouncement.nonce}`}>
        {bulkAnnouncement.text}
      </p>

      {/* UID-401 D9 — `remainingOnRfqCount` is passed ONLY for the two
       * cart-bound CTAs. For the original "Add N priced to cart" button the
       * remainder was never ambiguous (its own label and body copy already
       * say so), and passing it there would add a redundant line to a flow
       * this ticket did not change. */}
      <BulkAddPricedConfirmModal
        open={bulkConfirmOpen}
        count={cartEligibleCount}
        {...(bulkConfirmIntent === 'checkout' || bulkConfirmIntent === 'generate-quote'
          ? { remainingOnRfqCount: Math.max(0, lines.length - cartEligibleCount) }
          : {})}
        onCancel={handleBulkConfirmCancel}
        onConfirm={handleBulkConfirm}
      />

      <AddPartsModal
        open={addPartsOpen}
        search={searchAddPartsCandidates}
        alreadyAddedIds={alreadyAddedIds}
        onClose={() => setAddPartsOpen(false)}
        onAdd={handleAddParts}
        onUploadNewFiles={() => navigate('/uploads')}
      />

      {/* UID-262: the row menu only asks — this confirm step ("Move to a new
       * RFQ?", `.dc.html` L4469-4476) is what actually removes the part,
       * creates the new draft, and navigates to it (see handleMoveToNewRfq).
       * The part name comes from the pending line so the body copy can quote
       * it exactly as the design does. */}
      <MoveToNewRfqConfirmModal
        open={pendingMoveLineId !== undefined}
        partName={lines.find((line) => line.id === pendingMoveLineId)?.name ?? 'This part'}
        onCancel={() => setPendingMoveLineId(undefined)}
        onConfirm={() => {
          const id = pendingMoveLineId
          setPendingMoveLineId(undefined)
          if (id) {
            void handleMoveToNewRfq(id)
          }
        }}
      />

      {/* UID-201 (ADR-0076 Decision 4) — reuse the checkout add-modals: "＋ Add new shipping
       * account" writes to the saved shipping-account book, "＋ Add new address…" writes to the
       * shared account-wide address book; both auto-select the returned id (create-then-select). */}
      <AddShippingAccountModal
        open={showAddShippingAccount}
        onClose={() => setShowAddShippingAccount(false)}
        onSubmit={handleAddShippingAccount}
      />

      <AddressFormModal
        open={showAddAddress}
        onClose={() => setShowAddAddress(false)}
        onSubmit={handleAddAddress}
      />

      {/* UID-260/UID-346 (ADR-0085/ADR-0117) — the Bulk configure workflow,
       * opened from a per-row Configure button or the bulk toolbar's
       * "Configure N" (both just set `configureTargetIds`). The summary
       * rail's submit CTA does NOT open this — it stays disabled+tooltipped
       * while gated, per `.dc.html` (design source of truth), corrected
       * 2026-08-24.
       *
       * CONDITIONALLY MOUNTED (not kept mounted with `open` merely toggled)
       * so the modal's cascade state genuinely resets between openings
       * (AC5) — the same posture the parts vertical's identical modal's
       * caller (`PartsLibraryPage`) already takes for exactly this reason. */}
      {configureTargetIds !== undefined && (
        <ConfigurePartsModal
          open
          targets={configureTargetIds.flatMap((id) => {
            const target = lines.find((line) => line.id === id)
            return target ? [{ id: target.id, name: target.name }] : []
          })}
          onClose={() => setConfigureTargetIds(undefined)}
          onSave={async (lineIds, specification, quantity) => {
            const result = await handleSaveConfigure(lineIds, specification, quantity)
            if (result.ok) setConfigureTargetIds(undefined)
            return result
          }}
        />
      )}

      {/* UID-434/WS-D — the PER-LINE entry point, split out of the funnel
       * above: the per-row Configure button and the Requirements-cell
       * pencil (`PartsReviewTable`) both set `configureLineId`, routing here
       * instead of `ConfigurePartsModal`. Saves through the SAME
       * `handleSaveConfigure` seam (single-element id list) — no second save
       * path. CONDITIONALLY MOUNTED for the same reset-between-openings
       * reason as the bulk modal above. `line` is re-looked-up from `lines`
       * on every render (not memoized outside) so a successful save's
       * server-derived `specSelection` merge is reflected the next time this
       * reopens.
       *
       * UID-434/WS-D follow-up (this ticket) — the `onSave` wrapper below no
       * longer closes on every successful save; only `PartConfiguratorShell`
       * itself decides that (it already calls its own `onClose` prop for
       * "Save and close", `handleSave(true)`). Closing here unconditionally
       * was a pre-existing defect for "Save this configuration"
       * (`handleSave(false)`) — that secondary action is supposed to persist
       * and stay open (Part Library's identical shell action does), but this
       * wrapper's blanket `setConfigureLineId(undefined)` closed it anyway.
       * It also would have raced "Configure next part" below, which needs
       * the SAME successful save to advance to the next line instead of
       * closing. Fixed once, here, for both. */}
      {configureLineId !== undefined &&
        (() => {
          const line = lines.find((candidate) => candidate.id === configureLineId)
          if (!line) return null
          // UID-434/WS-D follow-up — "Configure next part" targeting,
          // single-sourced off the same `needsConfigureLineIds` predicate
          // `needsConfigureIds` above already uses (never a second
          // reimplementation of the dc.html `rfqNextToConfig` loop).
          //
          // Gate 5 Suggestion 10 — `nextLineId !== null` is the ONE real
          // fact; `nextPart` derives from it directly instead of being
          // split into a separately-passed boolean + callback pair.
          //
          // Gate 5 C3 (structural fix) contract — `onAdvance` below MUST
          // result in a changed `line` (i.e. `configureLineId`, and thus
          // `subject.id` inside the shell) on the next commit: it calls
          // `setConfigureLineId(nextLineId)`, and `nextLineId` is guaranteed
          // `!== configureLineId` here because `nextNeedsConfigureLineId`
          // searches for a DIFFERENT id than the one passed in. If this
          // call site is ever changed to re-target onto the SAME line (or to
          // not re-target at all), `PartConfiguratorShell` has no other way
          // to recover from `saving: true` — see
          // `PartConfiguratorShellProps.nextPart`'s doc comment.
          const nextLineId = nextNeedsConfigureLineId(lines, configureLineId)
          return (
            <RfqLineConfiguratorModal
              open
              line={line}
              nextPart={
                nextLineId !== null
                  ? { onAdvance: () => setConfigureLineId(nextLineId) }
                  : undefined
              }
              onClose={() => setConfigureLineId(undefined)}
              onSave={(lineId, specification, quantity) =>
                handleSaveConfigure([lineId], specification, quantity)
              }
            />
          )
        })()}
    </section>
  )
}
